import type { LucideIcon } from "lucide-react";
import {
  CheckCircle2,
  LayoutGrid,
  MonitorPlay,
  WifiOff,
  MessageSquare,
  Tv,
  Globe,
  Tablet,
  ShoppingBag,
  Clock,
  Package,
  Heart,
  Ticket,
  QrCode,
  Beer,
  MapPin,
  Users,
  Bike,
} from "lucide-react";

export type Vertical = "restaurant" | "nightlife";

type Bilingual = { en: string; es: string };

export interface FeatureExecutionStep {
  kicker: Bilingual;
  title: Bilingual;
  body: Bilingual;
}

export interface FeatureScreenshot {
  src: string;
  alt: Bilingual;
  caption?: Bilingual;
}

export interface MarketingFeature {
  slug: string;
  vertical: Vertical;
  icon: LucideIcon;
  accent?: string;
  card: {
    title: Bilingual;
    desc: Bilingual;
  };
  modal: {
    kicker: Bilingual;
    headline: Bilingual;
    body: Bilingual;
    highlights: Bilingual[];
  };
  page: {
    metaTitle: Bilingual;
    metaDescription: Bilingual;
    heroSubhead: Bilingual;
    whatYouGet: Bilingual[];
    execution: FeatureExecutionStep[];
    screenshots: FeatureScreenshot[];
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// RESTAURANT FEATURES
// ─────────────────────────────────────────────────────────────────────────────

export const features: MarketingFeature[] = [
  {
    slug: "zero-processing-fees",
    vertical: "restaurant",
    icon: CheckCircle2,
    accent: "#F58A1F",
    card: {
      title: { en: "Card Fees, Covered", es: "Fees de Tarjeta, Cubiertos" },
      desc: {
        en: "Dual pricing built in. Card payers cover the processing cost, so it doesn't come out of your margin.",
        es: "Dual pricing incorporado. Quien paga con tarjeta cubre el costo de procesamiento, así no sale de tu margen.",
      },
    },
    modal: {
      kicker: { en: "Dual pricing", es: "Dual pricing" },
      headline: {
        en: "Stop donating 2.6% of every card sale.",
        es: "Deja de donar 2.6% de cada venta con tarjeta.",
      },
      body: {
        en: "Redy shows the customer a card price and a cash price automatically. The card price is built to cover the processing cost, and customers who pay cash get a discount. It's the cash-discount model many gas stations use. Rules vary by state and card network, so check the signage your state requires.",
        es: "Redy muestra automáticamente un precio con tarjeta y otro en efectivo. El precio con tarjeta está pensado para cubrir el costo de procesamiento, y quien paga en efectivo recibe un descuento. Es el modelo de descuento en efectivo que usan muchas gasolineras. Las reglas cambian según el estado y la red de tarjetas, así que revisa los avisos que exige tu estado.",
      },
      highlights: [
        { en: "Cash-discount model: the menu shows the card price", es: "Modelo de descuento en efectivo: el menú muestra el precio con tarjeta" },
        { en: "Cash discount disclosed at checkout", es: "Descuento en efectivo declarado al cobrar" },
        { en: "Example: $20k/month in card sales at 2.6% is ~$520 a month off your margin", es: "Ejemplo: $20k/mes en ventas con tarjeta al 2.6% son ~$520 al mes que salen de tu margen" },
        { en: "Receipts show both prices for transparency", es: "Los recibos muestran ambos precios" },
      ],
    },
    page: {
      metaTitle: {
        en: "Dual Pricing: Card Payers Cover the Fee — Redy POS",
        es: "Dual Pricing: Quien Paga con Tarjeta Cubre el Fee — Redy POS",
      },
      metaDescription: {
        en: "Redy's dual pricing shows a card price and a cash price, so the processing cost is covered by customers who choose to pay by card. Check the signage rules in your state.",
        es: "El dual pricing de Redy muestra un precio con tarjeta y otro en efectivo, así el costo de procesamiento lo cubre quien elige pagar con tarjeta. Revisa las reglas de avisos de tu estado.",
      },
      heroSubhead: {
        en: "The customer chooses cash or card — and the math always works in your favor.",
        es: "El cliente elige efectivo o tarjeta — y la matemática siempre te favorece.",
      },
      whatYouGet: [
        { en: "Automatic cash-vs-card pricing on every receipt and screen", es: "Precio efectivo y tarjeta automáticos en cada pantalla y recibo" },
        { en: "Cash-discount note shown at online checkout", es: "Aviso del descuento en efectivo en el checkout online" },
        { en: "Cash discount shown as its own line on the receipt", es: "El descuento en efectivo aparece como línea propia en el recibo" },
        { en: "Works with Redy's North payment terminals", es: "Funciona con las terminales North de Redy" },
        { en: "Optional override for special customers (e.g. corporate)", es: "Override opcional para cuentas especiales (ej. corporativas)" },
      ],
      execution: [
        {
          kicker: { en: "Setup", es: "Setup" },
          title: { en: "Set up with your payments account", es: "Se configura con tu cuenta de pagos" },
          body: {
            en: "Redy sets up dual pricing together with your payments account, at 4% by default. You can see your program and rate any time in Settings → Payments.",
            es: "Redy configura el dual pricing junto con tu cuenta de pagos, al 4% por defecto. Ves tu programa y tu tasa cuando quieras en Configuración → Pagos.",
          },
        },
        {
          kicker: { en: "At checkout", es: "Al cobrar" },
          title: { en: "Both prices show automatically", es: "Aparecen ambos precios automáticamente" },
          body: {
            en: "Every cart screen, customer display and printed receipt shows the cash price and the card price side by side. The customer picks. You don't have to explain anything.",
            es: "Cada pantalla, customer display y recibo impreso muestra precio efectivo y precio tarjeta lado a lado. El cliente elige. Tú no tienes que explicar nada.",
          },
        },
        {
          kicker: { en: "On the receipt", es: "En el recibo" },
          title: { en: "Both prices, on paper", es: "Los dos precios, en papel" },
          body: {
            en: "If they pay cash, the receipt shows the cash discount as its own line, so the customer sees exactly what they saved.",
            es: "Si pagan en efectivo, el recibo muestra el descuento como línea propia, así el cliente ve exactamente cuánto se ahorró.",
          },
        },
        {
          kicker: { en: "End of month", es: "Fin de mes" },
          title: { en: "Read the report", es: "Lees el reporte" },
          body: {
            en: "The sales report shows your cash vs card mix, how much card payers covered, and an estimate of what the processor charged. The difference is the headline.",
            es: "El reporte de ventas te muestra el mix de efectivo vs tarjeta, cuánto cubrieron quienes pagaron con tarjeta y un estimado de lo que cobró el procesador. La diferencia es el titular.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/zero-processing-fees/01-checkout.png",
          alt: { en: "POS checkout screen showing cash and card prices side by side", es: "Pantalla de cobro mostrando precio efectivo y tarjeta lado a lado" },
          caption: { en: "Cash and card prices, side by side at checkout.", es: "Precio efectivo y tarjeta, lado a lado al cobrar." },
        },
        {
          src: "/marketing/features/zero-processing-fees/02-receipt.png",
          alt: { en: "Receipt with the cash-discount line", es: "Recibo con la línea de descuento en efectivo" },
          caption: { en: "Both prices, on the receipt.", es: "Los dos precios, en el recibo." },
        },
        {
          src: "/marketing/features/zero-processing-fees/03-report.png",
          alt: { en: "Dual pricing monthly savings report", es: "Reporte mensual de ahorro con Dual Pricing" },
          caption: { en: "Monthly report: what card payers covered.", es: "Reporte mensual: lo que cubrieron los pagos con tarjeta." },
        },
      ],
    },
  },
  {
    slug: "live-menu-boards",
    vertical: "restaurant",
    icon: Tv,
    card: {
      title: { en: "Live Menu Boards", es: "Menu Boards en Vivo" },
      desc: {
        en: "Change a price on the POS — it updates on every screen in your restaurant in 2 seconds. No InDesign, no reprints.",
        es: "Cambia un precio en el POS — se actualiza en cada pantalla de tu restaurante en 2 segundos. Sin reimprimir, sin diseñador.",
      },
    },
    modal: {
      kicker: { en: "Customer-facing displays", es: "Pantallas para clientes" },
      headline: {
        en: "Your menu, on every screen, always current.",
        es: "Tu menú, en todas las pantallas, siempre actualizado.",
      },
      body: {
        en: "Connect any TV, monitor or tablet to display your menu. When you change a price or 86 an item on the POS, every screen updates in seconds. No printing, no reprinting, no embarrassing crossed-out prices.",
        es: "Conecta cualquier TV, monitor o tablet para mostrar tu menú. Cuando cambias un precio o agotas un item en el POS, cada pantalla se actualiza en segundos. Sin imprimir, sin reimprimir, sin precios tachados.",
      },
      highlights: [
        { en: "Works on Fire TV, Android TV, or any browser", es: "Funciona en Fire TV, Android TV o cualquier browser" },
        { en: "Auto-86 sold-out items in real time", es: "86 automático de items agotados en tiempo real" },
        { en: "Scheduled menus (breakfast → lunch → dinner)", es: "Menús programados (desayuno → almuerzo → cena)" },
        { en: "Brand it with your logo, colors and photos", es: "Brándalo con tu logo, colores y fotos" },
        { en: "Unlimited screens — no per-display fee", es: "Pantallas ilimitadas — sin fee por display" },
      ],
    },
    page: {
      metaTitle: {
        en: "Live Menu Boards on Any TV — Redy POS",
        es: "Menu Boards en Vivo en Cualquier TV — Redy POS",
      },
      metaDescription: {
        en: "Show your menu on any TV. Change a price on the POS and every screen updates in seconds. Auto-86 sold-out items, schedule daypart menus, brand it your way.",
        es: "Muestra tu menú en cualquier TV. Cambia un precio en el POS y cada pantalla se actualiza en segundos. 86 automático, menús programados, branding tuyo.",
      },
      heroSubhead: {
        en: "Stop printing menus. Stop crossing out prices. Stop apologizing for the special that ran out two hours ago.",
        es: "Deja de imprimir menús. Deja de tachar precios. Deja de disculparte por el especial que se agotó hace dos horas.",
      },
      whatYouGet: [
        { en: "Live sync from POS → every display in <2 seconds", es: "Sync en vivo del POS → cada pantalla en <2 segundos" },
        { en: "Multiple layouts: grid, hero, rotating categories", es: "Layouts múltiples: grid, hero, categorías rotativas" },
        { en: "Schedule daypart menus by time of day", es: "Menús por daypart programados por hora del día" },
        { en: "Auto-hide items when stock hits zero", es: "Esconde items automáticamente cuando se agota el stock" },
        { en: "Per-location menus for multi-unit operators", es: "Menús por ubicación para multi-unidad" },
        { en: "Custom branding: logo, colors, hero image", es: "Branding custom: logo, colores, imagen hero" },
      ],
      execution: [
        {
          kicker: { en: "Step 1", es: "Paso 1" },
          title: { en: "Plug in a screen", es: "Conectas una pantalla" },
          body: {
            en: "Any TV with HDMI works. Add a Fire TV stick, or just open a browser on a spare laptop pointed at display.useredy.com/<your-slug>.",
            es: "Cualquier TV con HDMI sirve. Conecta un Fire TV stick, o simplemente abre un browser en una laptop apuntando a display.useredy.com/<tu-slug>.",
          },
        },
        {
          kicker: { en: "Step 2", es: "Paso 2" },
          title: { en: "Pick a layout", es: "Eliges un layout" },
          body: {
            en: "Choose a vertical hero, a 3-column grid or rotating categories. Drop in your logo, set your brand color, upload a hero image if you want.",
            es: "Elige vertical hero, grid 3 columnas o categorías rotativas. Pones tu logo, color de marca y una imagen hero si quieres.",
          },
        },
        {
          kicker: { en: "Step 3", es: "Paso 3" },
          title: { en: "Change a price, anywhere", es: "Cambias un precio, en cualquier parte" },
          body: {
            en: "Update an item on the POS, the admin, or even the kiosk. Every menu board in the restaurant refreshes in under 2 seconds. No staff intervention.",
            es: "Actualiza un item en el POS, el admin o hasta el kiosko. Cada menu board del restaurante se refresca en menos de 2 segundos. Sin intervención del staff.",
          },
        },
        {
          kicker: { en: "Step 4", es: "Paso 4" },
          title: { en: "Schedule the day", es: "Programas el día" },
          body: {
            en: "Set breakfast to switch to lunch at 11 AM and dinner at 5 PM. The board flips itself. You don't lift a finger.",
            es: "Programa desayuno → almuerzo a las 11 AM y cena a las 5 PM. El menu board cambia solo. No mueves un dedo.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/live-menu-boards/01-display.png",
          alt: { en: "Live menu board displayed on a TV screen", es: "Menu board en vivo en una TV" },
          caption: { en: "A TV menu board powered by Redy.", es: "Un menu board en TV con Redy." },
        },
        {
          src: "/marketing/features/live-menu-boards/02-editor.png",
          alt: { en: "Admin screen editing menu item price", es: "Pantalla admin editando precio de un item" },
          caption: { en: "Edit a price — every screen refreshes.", es: "Editas un precio — cada pantalla se refresca." },
        },
        {
          src: "/marketing/features/live-menu-boards/03-schedule.png",
          alt: { en: "Schedule for breakfast, lunch and dinner menus", es: "Programación de menús de desayuno, almuerzo y cena" },
          caption: { en: "Schedule dayparts — set and forget.", es: "Programa dayparts — configura y olvídate." },
        },
      ],
    },
  },
  {
    slug: "instant-website",
    vertical: "restaurant",
    icon: Globe,
    card: {
      title: { en: "Website, In Seconds", es: "Tu Sitio Web, En Segundos" },
      desc: {
        en: "A public site for your restaurant — synced live to your menu. No Squarespace, no developer, no monthly fee.",
        es: "Un sitio público para tu restaurante — sincronizado en vivo con tu menú. Sin Squarespace, sin developer, sin cuota mensual.",
      },
    },
    modal: {
      kicker: { en: "Free public website", es: "Sitio web público gratis" },
      headline: {
        en: "A real website. Synced to your menu. Free.",
        es: "Un sitio real. Sincronizado con tu menú. Gratis.",
      },
      body: {
        en: "The moment you finish onboarding, you have a public website at useredy.com/<your-slug> with your menu, hours, address, photos and a Google-friendly layout. Your real menu — live. Order online button included.",
        es: "El momento que terminas el onboarding, tienes un sitio público en useredy.com/<tu-slug> con tu menú, horarios, dirección, fotos y un layout Google-friendly. Tu menú real — en vivo. Botón de pedido online incluido.",
      },
      highlights: [
        { en: "Menu auto-synced from your POS", es: "Menú sincronizado automático desde tu POS" },
        { en: "Mobile-first, fast, SEO-ready", es: "Mobile-first, rápido, listo para SEO" },
        { en: "Custom domain (Plus and Pro) — yourrestaurant.com", es: "Dominio personalizado (Plus y Pro) — turestaurante.com" },
        { en: "Order online button built in", es: "Botón de pedido online integrado" },
        { en: "Photo gallery, hours, contact, Google Maps", es: "Galería, horarios, contacto, Google Maps" },
      ],
    },
    page: {
      metaTitle: {
        en: "Free Restaurant Website with Live Menu — Redy POS",
        es: "Sitio Web Gratis con Menú en Vivo — Redy POS",
      },
      metaDescription: {
        en: "Get a fast, mobile-first restaurant website synced live to your menu. No designer, no monthly fee. Custom domain on Plus.",
        es: "Sitio mobile-first sincronizado en vivo con tu menú. Sin diseñador, sin cuota mensual. Dominio personalizado con Plus.",
      },
      heroSubhead: {
        en: "You shouldn't pay a developer for a homepage that just shows your menu and your hours.",
        es: "No deberías pagarle a un developer por una homepage que solo muestra tu menú y tus horarios.",
      },
      whatYouGet: [
        { en: "Live menu (synced from POS, refreshes in seconds)", es: "Menú en vivo (sincronizado del POS, refresca en segundos)" },
        { en: "Hero photo, gallery, hours, address, contact form", es: "Foto hero, galería, horarios, dirección, formulario de contacto" },
        { en: "Mobile-first, page-speed optimized", es: "Mobile-first, optimizado para velocidad" },
        { en: "Order online button (direct, 0% fee)", es: "Botón de pedido online (directo, 0% fee)" },
        { en: "Google Business Profile-friendly schema", es: "Schema friendly para Google Business Profile" },
        { en: "Custom domain on Plus ($49/mo) and Pro", es: "Dominio personalizado en Plus ($49/mes) y Pro" },
      ],
      execution: [
        {
          kicker: { en: "Step 1", es: "Paso 1" },
          title: { en: "It's already live", es: "Ya está en vivo" },
          body: {
            en: "When you finish onboarding, your site is published. No deploy step. Just share useredy.com/<your-slug> with anyone.",
            es: "Cuando terminas el onboarding, tu sitio ya está publicado. Sin paso de deploy. Comparte useredy.com/<tu-slug> con quien quieras.",
          },
        },
        {
          kicker: { en: "Step 2", es: "Paso 2" },
          title: { en: "Drop in photos", es: "Subes fotos" },
          body: {
            en: "Upload a hero image and gallery photos from the admin. Add a tagline. The site picks it all up automatically.",
            es: "Sube imagen hero y galería desde el admin. Añade tagline. El sitio lo toma automáticamente.",
          },
        },
        {
          kicker: { en: "Step 3", es: "Paso 3" },
          title: { en: "Customers order from it", es: "Los clientes ordenan desde ahí" },
          body: {
            en: "The 'Order Online' button takes them straight to your menu, checkout and payment — 0% platform fee, 100% your customer.",
            es: "El botón 'Order Online' los lleva directo a tu menú, checkout y pago — 0% fee de plataforma, 100% tu cliente.",
          },
        },
        {
          kicker: { en: "Step 4", es: "Paso 4" },
          title: { en: "Upgrade to your domain", es: "Conectas tu dominio" },
          body: {
            en: "On Pro, point yourrestaurant.com at Redy. Your customers see your brand — no useredy.com in the URL.",
            es: "Con Pro, apuntas turestaurante.com a Redy. Tus clientes ven tu marca — sin useredy.com en la URL.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/instant-website/01-public-site.png",
          alt: { en: "Public restaurant website with live menu", es: "Sitio público del restaurante con menú en vivo" },
          caption: { en: "Your public site, ready to share.", es: "Tu sitio público, listo para compartir." },
        },
        {
          src: "/marketing/features/instant-website/02-mobile.png",
          alt: { en: "Mobile view of restaurant website", es: "Vista móvil del sitio del restaurante" },
          caption: { en: "Mobile-first by default.", es: "Mobile-first por defecto." },
        },
        {
          src: "/marketing/features/instant-website/03-domain.png",
          alt: { en: "Custom domain settings in admin", es: "Configuración de dominio personalizado en el admin" },
          caption: { en: "Bring your own domain on Plus or Pro.", es: "Trae tu propio dominio con Plus o Pro." },
        },
      ],
    },
  },
  {
    slug: "rush-hour-pos",
    vertical: "restaurant",
    icon: LayoutGrid,
    card: {
      title: { en: "Built for Rush Hour", es: "Para la Hora Pico" },
      desc: {
        en: "Any item in 2 taps. No lag, no crashes, no 'please wait' screens.",
        es: "Cualquier item en 2 taps. Sin lag, sin pantallas de espera.",
      },
    },
    modal: {
      kicker: { en: "POS performance", es: "Rendimiento del POS" },
      headline: {
        en: "A POS that doesn't slow down at 12:30.",
        es: "Un POS que no se cae a las 12:30.",
      },
      body: {
        en: "Redy was built by people who've worked the line. Every screen is one tap deep, the iPad never spins, and you can ring an order while someone else is paying. When a 30-person line walks in, the POS doesn't blink.",
        es: "Redy fue construido por gente que ha trabajado la línea. Cada pantalla está a un tap, el iPad nunca se queda pensando, y puedes tomar una orden mientras otro está pagando. Cuando entran 30 personas, el POS no parpadea.",
      },
      highlights: [
        { en: "Any item, any modifier — 2 taps max", es: "Cualquier item, cualquier modificador — máximo 2 taps" },
        { en: "Multi-cart: ring while teammate cashes out", es: "Multi-cart: cobras y otro toma orden a la vez" },
        { en: "Held orders, splits, comps, voids — built in", es: "Órdenes en espera, splits, comps, voids — integrado" },
        { en: "PIN-protected discounts and supervisor actions", es: "Descuentos y supervisor con PIN" },
        { en: "Custom items and quick-add buttons", es: "Items custom y botones rápidos" },
      ],
    },
    page: {
      metaTitle: {
        en: "POS Built for Rush Hour — Redy",
        es: "POS Hecho para la Hora Pico — Redy",
      },
      metaDescription: {
        en: "Any item in 2 taps. Multi-cart, held orders, splits, comps. Built for restaurants where every second of the rush counts.",
        es: "Cualquier item en 2 taps. Multi-cart, órdenes en espera, splits, comps. Hecho para restaurantes donde cada segundo cuenta.",
      },
      heroSubhead: {
        en: "Your POS shouldn't be a bottleneck during the lunch rush.",
        es: "Tu POS no debería ser un cuello de botella en la hora pico.",
      },
      whatYouGet: [
        { en: "Visual menu grid with photos, categories, modifiers", es: "Grid visual con fotos, categorías, modificadores" },
        { en: "Held orders queue (for delayed takeout, table holds)", es: "Cola de órdenes en espera (takeout retrasado, mesas)" },
        { en: "Split builder: split by item, by seat, by percentage", es: "Split builder: por item, por silla, por porcentaje" },
        { en: "Custom items mid-order without leaving the cart", es: "Items custom sin salir del carrito" },
        { en: "Tip screen, signature capture, cash drawer", es: "Pantalla de propina, captura de firma, cash drawer" },
        { en: "Cash sales keep going if WiFi drops mid-rush", es: "Las ventas en efectivo siguen si cae el WiFi en plena hora pico" },
      ],
      execution: [
        {
          kicker: { en: "Open", es: "Abres" },
          title: { en: "Tap the menu grid", es: "Tap en el grid del menú" },
          body: {
            en: "The home screen IS the menu. Photo grid with categories. No hunting through three submenus.",
            es: "La pantalla home ES el menú. Grid con fotos por categoría. Sin buscar entre tres submenús.",
          },
        },
        {
          kicker: { en: "Add", es: "Agregas" },
          title: { en: "Modifiers in one sheet", es: "Modificadores en una sola hoja" },
          body: {
            en: "Tap an item — modifiers open as a single bottom sheet. Pick everything you need, hit Add. Done.",
            es: "Tap en el item — los modificadores abren como una sola hoja inferior. Eliges todo, presionas Agregar. Listo.",
          },
        },
        {
          kicker: { en: "Pay", es: "Cobras" },
          title: { en: "Split or full — your call", es: "Split o completo — tú decides" },
          body: {
            en: "Cash, card, dual price, split between people, leave a tab open — every flow is one tap away from the cart.",
            es: "Efectivo, tarjeta, dual price, split entre personas, dejar tab abierta — cada flujo está a un tap del carrito.",
          },
        },
        {
          kicker: { en: "Hold", es: "Mantén" },
          title: { en: "Multi-cart for the rush", es: "Multi-cart para la hora pico" },
          body: {
            en: "Customer's not ready? Hold the cart. Take the next person. Come back to the first cart when they decide. No friction.",
            es: "¿El cliente no decide? Pones la orden en espera. Atiendes al siguiente. Vuelves cuando deciden. Sin fricción.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/rush-hour-pos/01-grid.png",
          alt: { en: "Menu grid with photos", es: "Grid del menú con fotos" },
          caption: { en: "Visual menu — every item one tap away.", es: "Menú visual — cada item a un tap." },
        },
        {
          src: "/marketing/features/rush-hour-pos/02-modifiers.png",
          alt: { en: "Modifier bottom sheet", es: "Hoja de modificadores" },
          caption: { en: "Modifiers in a single sheet.", es: "Modificadores en una sola hoja." },
        },
        {
          src: "/marketing/features/rush-hour-pos/03-split.png",
          alt: { en: "Split builder screen", es: "Pantalla del split builder" },
          caption: { en: "Split by item, by seat, or by percent.", es: "Split por item, silla o porcentaje." },
        },
      ],
    },
  },
  {
    slug: "kitchen-display-system",
    vertical: "restaurant",
    icon: MonitorPlay,
    card: {
      title: { en: "Kitchen Display (KDS)", es: "Pantalla de Cocina" },
      desc: {
        en: "Cooks see orders instantly. Multi-station routing. No printer jams, no lost tickets.",
        es: "Los cocineros ven las órdenes al instante. Ruteo multi-estación. Sin tickets perdidos.",
      },
    },
    modal: {
      kicker: { en: "Multi-station KDS", es: "KDS multi-estación" },
      headline: {
        en: "Every cook gets their station, not the whole ticket.",
        es: "Cada cocinero ve su estación, no todo el ticket.",
      },
      body: {
        en: "Route orders by station — grill sees grill items, salads sees salads, the expo sees everything. Tickets bump on done, timers run live, modifiers are big and clear. No more lost paper tickets, no more 'where's my fries?'.",
        es: "Rutea órdenes por estación — grill ve los grill items, salads ve ensaladas, expo ve todo. Tickets se bumpean al terminar, los timers corren en vivo, los modificadores son grandes y claros. Sin tickets perdidos, sin '¿dónde están las papas?'.",
      },
      highlights: [
        { en: "Per-station routing (grill, fryer, salad, expo)", es: "Ruteo por estación (grill, freidora, ensalada, expo)" },
        { en: "Live timers per ticket, alerts when stale", es: "Timers en vivo por ticket, alertas si se demora" },
        { en: "Modifier and note emphasis", es: "Énfasis en modificadores y notas" },
        { en: "Bump-to-done with a tap", es: "Bump-to-done con un tap" },
        { en: "Recall a ticket if the customer changes mind", es: "Recall de ticket si el cliente cambia" },
      ],
    },
    page: {
      metaTitle: {
        en: "Kitchen Display System (KDS) — Multi-Station, Included | Redy POS",
        es: "Kitchen Display (KDS) — Multi-Estación Incluido | Redy POS",
      },
      metaDescription: {
        en: "Multi-station kitchen display included free. Route orders by station, run live timers, bump tickets and never lose a paper slip again.",
        es: "Kitchen Display multi-estación incluido gratis. Rutea por estación, timers en vivo, bump de tickets y nunca pierdes un ticket impreso.",
      },
      heroSubhead: {
        en: "Many POS systems charge extra for a kitchen display. Redy includes it.",
        es: "Muchos POS cobran aparte la pantalla de cocina. Redy la incluye.",
      },
      whatYouGet: [
        { en: "Unlimited stations on any iPad or browser", es: "Estaciones ilimitadas en cualquier iPad o browser" },
        { en: "Per-station routing rules per menu item", es: "Reglas de ruteo por estación por item de menú" },
        { en: "Color-coded urgency (green → yellow → red)", es: "Urgencia por color (verde → amarillo → rojo)" },
        { en: "Notes and key modifiers highlighted at top", es: "Notas y modificadores clave resaltados arriba" },
        { en: "Recall, void, comp flags pushed in real time", es: "Recall, void y comp empujados en vivo" },
        { en: "Expo screen that aggregates every station", es: "Pantalla de expo que agrega todas las estaciones" },
      ],
      execution: [
        {
          kicker: { en: "Setup", es: "Setup" },
          title: { en: "Define your stations", es: "Defines tus estaciones" },
          body: {
            en: "From Admin → Kitchen → Stations, create Grill, Fryer, Salad, Expo. Drag menu categories to the right stations.",
            es: "En Admin → Cocina → Estaciones, crea Grill, Freidora, Ensalada, Expo. Arrastra categorías del menú a la estación correcta.",
          },
        },
        {
          kicker: { en: "Service", es: "Servicio" },
          title: { en: "Orders fan out", es: "Las órdenes se distribuyen" },
          body: {
            en: "When a server fires an order, each station sees only the items it owns. Expo sees the whole ticket so they can plate and call it.",
            es: "Cuando el server dispara una orden, cada estación ve solo sus items. Expo ve el ticket completo para emplatar y cantar.",
          },
        },
        {
          kicker: { en: "Bump", es: "Bump" },
          title: { en: "Tap when done", es: "Tap cuando termina" },
          body: {
            en: "The cook taps to mark their portion done. The ticket grays out on their station. Expo sees green when every station is bumped.",
            es: "El cocinero da tap para marcar su parte lista. El ticket se opaca en su estación. Expo lo ve verde cuando todas terminaron.",
          },
        },
        {
          kicker: { en: "Audit", es: "Auditas" },
          title: { en: "Read the report", es: "Lees el reporte" },
          body: {
            en: "End of shift, see avg ticket time per station, longest waits, and where the bottleneck was. Tune your line.",
            es: "Fin del turno, ves promedio por estación, esperas más largas y dónde estuvo el cuello de botella. Ajustas tu línea.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/kitchen-display-system/01-station.png",
          alt: { en: "KDS station view with multiple tickets", es: "Vista de estación KDS con tickets" },
          caption: { en: "A grill station view — only what they cook.", es: "Vista del grill — solo lo que cocinan." },
        },
        {
          src: "/marketing/features/kitchen-display-system/02-expo.png",
          alt: { en: "Expo screen aggregating all stations", es: "Pantalla expo agregando todas las estaciones" },
          caption: { en: "Expo screen — the whole ticket, all stations.", es: "Pantalla expo — el ticket completo, todas las estaciones." },
        },
        {
          src: "/marketing/features/kitchen-display-system/03-report.png",
          alt: { en: "KDS performance report", es: "Reporte de desempeño del KDS" },
          caption: { en: "End-of-shift station performance.", es: "Desempeño por estación al cierre.." },
        },
      ],
    },
  },
  {
    slug: "self-order-kiosk",
    vertical: "restaurant",
    icon: Tablet,
    card: {
      title: { en: "Self-Order Kiosk", es: "Kiosko de Auto-Servicio" },
      desc: {
        en: "Turn any spare iPad into a self-order kiosk. No extra cost, no separate app, no licensing.",
        es: "Convierte cualquier iPad en kiosko de auto-servicio. Sin costo extra, sin licencias, sin app aparte.",
      },
    },
    modal: {
      kicker: { en: "Kiosk mode", es: "Modo kiosko" },
      headline: {
        en: "Add a cashier without hiring a cashier.",
        es: "Agrega un cajero sin contratar un cajero.",
      },
      body: {
        en: "Switch any iPad to Kiosk mode and you have a self-serve ordering station. Attract screen, menu browse, modifiers, payment, confirmation — all in your branding. Idle timeout resets between guests. Orders flow into the same KDS as your counter.",
        es: "Cambia cualquier iPad a modo Kiosko y tienes una estación de auto-servicio. Pantalla de atracción, menú, modificadores, pago, confirmación — todo en tu marca. Idle timeout resetea entre clientes. Las órdenes entran al mismo KDS que el counter.",
      },
      highlights: [
        { en: "Branded attract screen with looping video", es: "Pantalla de atracción con video en loop" },
        { en: "Same menu, modifiers and prices as your POS", es: "Mismo menú, modificadores y precios que el POS" },
        { en: "Idle timeout resets cart between guests", es: "Idle timeout limpia el carrito entre clientes" },
        { en: "Card and dual-pricing supported", es: "Tarjeta y dual pricing soportados" },
        { en: "Orders fire to KDS like any POS ticket", es: "Las órdenes van al KDS como cualquier ticket" },
      ],
    },
    page: {
      metaTitle: {
        en: "Self-Order Kiosk on Any iPad — Redy POS",
        es: "Kiosko de Auto-Servicio en Cualquier iPad — Redy POS",
      },
      metaDescription: {
        en: "Turn a spare iPad into a self-order kiosk. Same menu as your POS, branded attract screen, idle timeout, card payment built in. No extra fee.",
        es: "Convierte cualquier iPad en kiosko de auto-servicio. Mismo menú del POS, atracción brandeada, idle timeout, pago con tarjeta integrado. Sin fee extra.",
      },
      heroSubhead: {
        en: "Dedicated kiosk hardware and licenses add up fast. Turn the iPad you already have into one.",
        es: "El hardware y las licencias de kiosko suman rápido. Convierte en kiosko el iPad que ya tienes.",
      },
      whatYouGet: [
        { en: "Full self-order experience: attract → menu → checkout", es: "Experiencia completa: atracción → menú → checkout" },
        { en: "Brand it with your logo, hero video, colors", es: "Brándalo con tu logo, video hero, colores" },
        { en: "Customize the attract screen with promo items", es: "Personaliza la atracción con items promocionales" },
        { en: "Idle warning + auto-reset between guests", es: "Aviso de idle + auto-reset entre clientes" },
        { en: "Card payment via the same North terminal", es: "Pago con tarjeta vía el mismo terminal North" },
        { en: "Customer name capture + SMS when ready", es: "Captura nombre del cliente + SMS cuando esté lista" },
      ],
      execution: [
        {
          kicker: { en: "Step 1", es: "Paso 1" },
          title: { en: "Flip the iPad to Kiosk mode", es: "Cambias el iPad a modo Kiosko" },
          body: {
            en: "From the POS, hit the menu → 'Switch to Kiosk'. The iPad locks into kiosk UI until a manager PIN unlocks it.",
            es: "Desde el POS, menú → 'Cambiar a Kiosko'. El iPad se bloquea en UI de kiosko hasta que un manager lo desbloquee con PIN.",
          },
        },
        {
          kicker: { en: "Step 2", es: "Paso 2" },
          title: { en: "Customer walks up", es: "El cliente llega" },
          body: {
            en: "They see your attract screen — looping video or hero image. They tap to start. The menu opens with your real categories and prices.",
            es: "Ven tu pantalla de atracción — video en loop o imagen hero. Dan tap para empezar. El menú abre con tus categorías y precios reales.",
          },
        },
        {
          kicker: { en: "Step 3", es: "Paso 3" },
          title: { en: "They build their order", es: "Construyen su orden" },
          body: {
            en: "Browse, pick modifiers, review cart. Add their name. Pay by tap or chip on the same North terminal you use for the counter.",
            es: "Navegan, eligen modificadores, revisan carrito. Ponen su nombre. Pagan con tap o chip en el mismo terminal North del counter.",
          },
        },
        {
          kicker: { en: "Step 4", es: "Paso 4" },
          title: { en: "Order fires to KDS", es: "La orden va al KDS" },
          body: {
            en: "Same as any POS ticket. They get a confirmation screen, optional SMS, and the kiosk resets for the next guest after a few idle seconds.",
            es: "Como cualquier ticket. Reciben pantalla de confirmación, SMS opcional, y el kiosko resetea para el siguiente tras unos segundos de idle.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/self-order-kiosk/01-attract.png",
          alt: { en: "Kiosk attract screen with hero video", es: "Pantalla de atracción del kiosko con video hero" },
          caption: { en: "Branded attract screen.", es: "Pantalla de atracción brandeada." },
        },
        {
          src: "/marketing/features/self-order-kiosk/02-menu.png",
          alt: { en: "Kiosk menu browsing", es: "Navegación del menú en el kiosko" },
          caption: { en: "Customer browses your real menu.", es: "El cliente navega tu menú real." },
        },
        {
          src: "/marketing/features/self-order-kiosk/03-payment.png",
          alt: { en: "Kiosk payment screen", es: "Pantalla de pago en el kiosko" },
          caption: { en: "Tap or chip on the same terminal.", es: "Tap o chip en el mismo terminal." },
        },
      ],
    },
  },
  {
    slug: "online-ordering",
    vertical: "restaurant",
    icon: ShoppingBag,
    card: {
      title: { en: "Online Ordering — 0% Fee", es: "Pedidos en Línea — 0% Fee" },
      desc: {
        en: "Customers order directly from your site. No marketplace commission of up to 30% — and you keep the customer relationship.",
        es: "Los clientes ordenan desde tu sitio. Sin comisión de marketplace de hasta 30% — y la relación con el cliente es tuya.",
      },
    },
    modal: {
      kicker: { en: "Direct online ordering", es: "Pedidos en línea directos" },
      headline: {
        en: "Take orders from your own site. Pay 0% platform fee.",
        es: "Recibe pedidos desde tu propio sitio. Paga 0% de fee.",
      },
      body: {
        en: "Your customers go to your site, browse your menu, pay with card or Apple Pay, and the order lands on your POS and KDS like any other ticket. Marketplace apps can take up to 30%. We take 0%.",
        es: "Tus clientes van a tu sitio, navegan tu menú, pagan con tarjeta o Apple Pay, y la orden cae en tu POS y KDS como cualquier ticket. Las apps de marketplace pueden cobrar hasta 30%. Nosotros 0%.",
      },
      highlights: [
        { en: "Pickup and dine-in supported (delivery in pilot)", es: "Pickup y dine-in soportados (delivery en piloto)" },
        { en: "Apple Pay, Google Pay, card — checkout in seconds", es: "Apple Pay, Google Pay, tarjeta — checkout en segundos" },
        { en: "Order lands on your POS + KDS, just like in-store", es: "La orden cae en tu POS + KDS como en tienda" },
        { en: "Customer captures: phone, email, name", es: "Capturas: teléfono, email, nombre del cliente" },
        { en: "SMS confirmation + ready notification", es: "Confirmación + aviso de listo por SMS" },
      ],
    },
    page: {
      metaTitle: {
        en: "Online Ordering with 0% Platform Fee — Redy POS",
        es: "Pedidos en Línea con 0% de Fee — Redy POS",
      },
      metaDescription: {
        en: "Take pickup and dine-in orders from your own website. Apple Pay, Google Pay, card. 0% platform fee. Orders land on your POS and KDS automatically.",
        es: "Recibe pedidos de pickup y dine-in desde tu sitio. Apple Pay, Google Pay, tarjeta. 0% fee. Las órdenes caen en tu POS y KDS automáticamente.",
      },
      heroSubhead: {
        en: "Why pay a marketplace up to 30% for a customer who already lives down the street?",
        es: "¿Por qué pagarle hasta 30% a un marketplace por un cliente que ya vive en tu cuadra?",
      },
      whatYouGet: [
        { en: "Your menu, your prices, your branding", es: "Tu menú, tus precios, tu branding" },
        { en: "Apple Pay + Google Pay + card via North", es: "Apple Pay + Google Pay + tarjeta vía North" },
        { en: "Pickup time selector + dine-in QR ordering", es: "Selector de hora de pickup + dine-in con QR" },
        { en: "Order routes to POS and KDS automatically", es: "La orden rutea al POS y KDS automáticamente" },
        { en: "Email confirmation to merchant + customer SMS", es: "Email al merchant + SMS al cliente" },
        { en: "Customer database grows with every order", es: "Tu base de clientes crece con cada pedido" },
      ],
      execution: [
        {
          kicker: { en: "Step 1", es: "Paso 1" },
          title: { en: "Switch on Online Ordering", es: "Activas pedidos en línea" },
          body: {
            en: "From Admin → Online Ordering, set pickup hours, prep times and any item availability rules. Hit Enable.",
            es: "En Admin → Pedidos en Línea, configuras horarios de pickup, tiempos de prep y reglas de disponibilidad. Le das Enable.",
          },
        },
        {
          kicker: { en: "Step 2", es: "Paso 2" },
          title: { en: "Customer orders", es: "El cliente ordena" },
          body: {
            en: "They tap 'Order Online' on your site, browse the same menu, pay with Apple Pay or card. Total time: 90 seconds.",
            es: "Dan tap a 'Order Online' en tu sitio, navegan el mismo menú, pagan con Apple Pay o tarjeta. Tiempo total: 90 segundos.",
          },
        },
        {
          kicker: { en: "Step 3", es: "Paso 3" },
          title: { en: "Order hits your kitchen", es: "La orden llega a tu cocina" },
          body: {
            en: "Lands on the KDS with a pickup time stamp. Cooks fire it on schedule. POS shows it in the orders queue.",
            es: "Cae en el KDS con timestamp de pickup. Los cocineros la disparan según horario. POS la muestra en la cola de órdenes.",
          },
        },
        {
          kicker: { en: "Step 4", es: "Paso 4" },
          title: { en: "Customer gets a text", es: "El cliente recibe un texto" },
          body: {
            en: "When it's ready, the customer's phone buzzes — 'Your order is ready for pickup'. Done.",
            es: "Cuando está lista, el teléfono del cliente vibra — 'Tu orden está lista para recoger'. Fin.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/online-ordering/01-site.png",
          alt: { en: "Customer ordering from restaurant website", es: "Cliente ordenando desde el sitio del restaurante" },
          caption: { en: "Customer ordering directly from your site.", es: "Cliente ordenando directo de tu sitio." },
        },
        {
          src: "/marketing/features/online-ordering/02-checkout.png",
          alt: { en: "Checkout with Apple Pay", es: "Checkout con Apple Pay" },
          caption: { en: "Apple Pay checkout in seconds.", es: "Checkout con Apple Pay en segundos." },
        },
        {
          src: "/marketing/features/online-ordering/03-kds.png",
          alt: { en: "Order landing on KDS with pickup time", es: "Orden en el KDS con hora de pickup" },
          caption: { en: "Order lands on KDS with pickup time.", es: "Orden en KDS con hora de pickup." },
        },
      ],
    },
  },
  {
    slug: "delivery",
    vertical: "restaurant",
    icon: Bike,
    card: {
      title: { en: "Delivery — 0% Commission", es: "Delivery — 0% Comisión" },
      desc: {
        en: "Couriers from Uber Direct deliver your order. The customer pays the courier, you pay from $2.99 (by plan). No commission on the food. Pilot: opening location by location.",
        es: "Couriers de Uber Direct entregan tu pedido. El cliente paga el envío, tú pagas desde $2.99 (según tu plan). Sin comisión sobre la comida. Piloto: se abre local por local.",
      },
    },
    modal: {
      kicker: { en: "First-party delivery", es: "Delivery propio" },
      headline: {
        en: "Stop paying 25% commission on the customers you already had.",
        es: "Deja de pagar 25% de comisión por los clientes que ya tenías.",
      },
      body: {
        en: "Your regulars order from your own page. When the kitchen marks it ready, a courier from Uber Direct picks it up. The customer pays the real courier quote, shown before they pay. You pay Redy from $2.99 per delivered delivery, by plan, and 0% commission on the order. Keep DoorDash for discovery if you want; bring your regulars home. Delivery is in pilot and Redy turns it on location by location.",
        es: "Tus clientes de siempre ordenan en tu propia página. Cuando la cocina la marca lista, un courier de Uber Direct la recoge. El cliente paga la cotización real del courier, que ve antes de pagar. Tú le pagas a Redy desde $2.99 por envío entregado, según tu plan, y 0% de comisión sobre el pedido. Quédate con DoorDash para descubrimiento si quieres; trae a tus regulares a casa. Delivery está en piloto y Redy lo enciende local por local.",
      },
      highlights: [
        { en: "0% commission on the order value", es: "0% de comisión sobre el valor del pedido" },
        { en: "From $2.99 per delivered delivery — nothing else", es: "Desde $2.99 por envío entregado — nada más" },
        { en: "Cancelled delivery: Redy charges you nothing", es: "Envío cancelado: Redy no te cobra nada" },
        { en: "Couriers from Uber Direct", es: "Couriers de Uber Direct" },
        { en: "The customer, their data and the branding stay yours", es: "El cliente, sus datos y el branding siguen siendo tuyos" },
      ],
    },
    page: {
      metaTitle: {
        en: "Delivery with 0% Commission — Redy POS",
        es: "Delivery con 0% de Comisión — Redy POS",
      },
      metaDescription: {
        en: "First-party delivery on Uber's courier network. 0% commission, from $2.99 per delivered delivery. On a $45 order you keep $42.01 instead of $33.75.",
        es: "Delivery propio sobre la red de couriers de Uber. 0% de comisión, desde $2.99 por envío entregado. En un pedido de $45 te quedas con $42.01 en vez de $33.75.",
      },
      heroSubhead: {
        en: "DoorDash is good at finding new customers. It's expensive at delivering to the ones you already had.",
        es: "DoorDash es bueno para encontrar clientes nuevos. Es caro para entregarle a los que ya tenías.",
      },
      whatYouGet: [
        { en: "0% commission on every delivery order", es: "0% de comisión en cada pedido a domicilio" },
        { en: "From $2.99 per delivered delivery", es: "Desde $2.99 por envío entregado" },
        { en: "Uber Direct courier network, dispatched automatically", es: "Red de couriers de Uber Direct, despachada automáticamente" },
        { en: "Live tracking link for the customer", es: "Link de seguimiento en vivo para el cliente" },
        { en: "Your real menu prices — nothing inflated", es: "Tus precios reales de menú — nada inflado" },
        { en: "Pilot: Redy enables it per location; then you control it from your panel", es: "Piloto: Redy lo habilita por local; después lo controlas desde tu panel" },
      ],
      execution: [
        {
          kicker: { en: "Step 1", es: "Paso 1" },
          title: { en: "The customer orders on your page", es: "El cliente ordena en tu página" },
          body: {
            en: "Same site, same menu, same prices. They type an address, Redy quotes the courier live and shows the real cost before they pay.",
            es: "Mismo sitio, mismo menú, mismos precios. Escriben una dirección, Redy cotiza el courier en vivo y muestra el costo real antes de pagar.",
          },
        },
        {
          kicker: { en: "Step 2", es: "Paso 2" },
          title: { en: "The kitchen marks it ready", es: "La cocina la marca lista" },
          body: {
            en: "The ticket lands on the KDS like any other. When the kitchen bumps it, Redy calls the courier — nobody has to remember to do it.",
            es: "El ticket cae en el KDS como cualquier otro. Cuando la cocina lo bumpea, Redy llama al courier — nadie tiene que acordarse de hacerlo.",
          },
        },
        {
          kicker: { en: "Step 3", es: "Paso 3" },
          title: { en: "An Uber courier picks it up", es: "Un courier de Uber la recoge" },
          body: {
            en: "A courier from Uber Direct walks in, takes the bag, and leaves. No driver to hire, no car insurance, no scheduling.",
            es: "Un courier de Uber Direct entra, toma la bolsa y sale. Sin repartidor que contratar, sin seguro de auto, sin horarios.",
          },
        },
        {
          kicker: { en: "Step 4", es: "Paso 4" },
          title: { en: "The customer tracks it live", es: "El cliente la sigue en vivo" },
          body: {
            en: "A tracking link on your page, with your name on it. If the delivery gets cancelled, Redy charges you nothing for it.",
            es: "Un link de seguimiento en tu página, con tu nombre. Si el envío se cancela, Redy no te cobra nada por él.",
          },
        },
      ],
      screenshots: [],
    },
  },
  {
    slug: "employee-time-clock",
    vertical: "restaurant",
    icon: Clock,
    card: {
      title: { en: "Employee Time Clock", es: "Reloj de Empleados" },
      desc: {
        en: "Clock-in/out by PIN, automatic timesheets, schedule, and payroll reports. All in the same POS.",
        es: "Clock-in/out por PIN, timesheets automáticos, horarios y reportes de nómina. Todo en el mismo POS.",
      },
    },
    modal: {
      kicker: { en: "Time & attendance", es: "Reloj y asistencia" },
      headline: {
        en: "Stop paying When I Work.",
        es: "Deja de pagar When I Work.",
      },
      body: {
        en: "Every employee gets a PIN. They clock in from any POS, kiosk, or back-office tablet. Timesheets build themselves. Schedule lives in the admin. Hours, overtime and tips come out in a spreadsheet for whoever runs your payroll.",
        es: "Cada empleado tiene un PIN. Clock-in desde cualquier POS, kiosko o tablet de back-office. Los timesheets se construyen solos. El horario vive en el admin. Las horas, el overtime y las propinas salen en una planilla para quien corre tu nómina.",
      },
      highlights: [
        { en: "PIN clock-in from any device", es: "Clock-in por PIN desde cualquier dispositivo" },
        { en: "Automatic timesheets — no manual entry", es: "Timesheets automáticos — sin entrada manual" },
        { en: "Schedule builder with drag-and-drop shifts", es: "Constructor de horarios drag-and-drop" },
        { en: "Payroll export with hours, overtime and tips", es: "Export de nómina con horas, overtime y propinas" },
        { en: "Tips, breaks, overtime tracked automatically", es: "Propinas, descansos, overtime trackeados automáticamente" },
      ],
    },
    page: {
      metaTitle: {
        en: "Employee Time Clock + Timesheets — Redy POS",
        es: "Reloj de Empleados + Timesheets — Redy POS",
      },
      metaDescription: {
        en: "PIN clock-in/out, automatic timesheets, drag-and-drop schedule, and exports with hours, overtime and tips. Replace When I Work. Included free.",
        es: "Clock-in/out por PIN, timesheets automáticos, horario drag-and-drop y export de horas, overtime y propinas. Reemplaza When I Work. Incluido gratis.",
      },
      heroSubhead: {
        en: "The hours go in. The schedule comes out. Payroll prep takes minutes, not hours.",
        es: "Los horarios entran. El schedule sale. Preparar la nómina toma minutos, no horas.",
      },
      whatYouGet: [
        { en: "Per-employee PIN, role and pay rate", es: "PIN, rol y tarifa por empleado" },
        { en: "Clock-in from POS, KDS or back-office tablet", es: "Clock-in desde POS, KDS o tablet" },
        { en: "Auto-calculated overtime and break compliance", es: "Overtime y descansos calculados automáticamente" },
        { en: "Weekly schedule builder", es: "Constructor de horario semanal" },
        { en: "Payroll report grouped by employee + period", es: "Reporte de nómina agrupado por empleado y periodo" },
        { en: "Tip pool calculator and tip-out report", es: "Calculadora de pool de propinas y reporte de tip-out" },
      ],
      execution: [
        {
          kicker: { en: "Setup", es: "Setup" },
          title: { en: "Add your team", es: "Agregas tu equipo" },
          body: {
            en: "From Admin → Employees, add each person with their role, pay rate and a 4-digit PIN they'll use to clock in.",
            es: "En Admin → Empleados, agrega cada persona con rol, tarifa y un PIN de 4 dígitos para clock-in.",
          },
        },
        {
          kicker: { en: "Open", es: "Apertura" },
          title: { en: "They clock in", es: "Ellos hacen clock-in" },
          body: {
            en: "Tap their initials on any iPad, enter PIN, they're clocked in. Same flow at the end of shift to clock out.",
            es: "Dan tap a sus iniciales en cualquier iPad, ingresan PIN, están clockeados. Mismo flujo al final del turno.",
          },
        },
        {
          kicker: { en: "Service", es: "Servicio" },
          title: { en: "Timesheets build themselves", es: "Los timesheets se construyen solos" },
          body: {
            en: "Every clock-in and clock-out feeds the timesheet. Manager can correct an entry from the admin if someone forgot to clock out.",
            es: "Cada clock-in/out alimenta el timesheet. El manager corrige una entrada desde el admin si alguien olvidó clock-out.",
          },
        },
        {
          kicker: { en: "Pay", es: "Nómina" },
          title: { en: "Export payroll", es: "Exportas nómina" },
          body: {
            en: "End of pay period, hit Export. A spreadsheet with hours, overtime and tips per employee. Hand it to whoever runs your payroll. Done.",
            es: "Fin de periodo, le das Export. Una planilla con horas, overtime y propinas por empleado. Se la pasas a quien corre tu nómina. Listo.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/employee-time-clock/01-clock-in.png",
          alt: { en: "Employee PIN clock-in screen", es: "Pantalla de clock-in por PIN" },
          caption: { en: "PIN clock-in from any iPad.", es: "Clock-in por PIN desde cualquier iPad." },
        },
        {
          src: "/marketing/features/employee-time-clock/02-schedule.png",
          alt: { en: "Schedule builder", es: "Constructor de horario" },
          caption: { en: "Drag-and-drop the week's schedule.", es: "Drag-and-drop del horario de la semana." },
        },
        {
          src: "/marketing/features/employee-time-clock/03-payroll.png",
          alt: { en: "Payroll report", es: "Reporte de nómina" },
          caption: { en: "Payroll report — export or print.", es: "Reporte de nómina — exporta o imprime." },
        },
      ],
    },
  },
  {
    slug: "sms-customers-staff",
    vertical: "restaurant",
    icon: MessageSquare,
    card: {
      title: { en: "SMS for Customers & Staff", es: "SMS para Clientes y Staff" },
      desc: {
        en: "Customers get a text when their order is ready. Your staff gets their shifts by text when you publish the schedule.",
        es: "Los clientes reciben SMS cuando su orden está lista. Tu staff recibe sus turnos por SMS cuando publicas el horario.",
      },
    },
    modal: {
      kicker: { en: "Built-in SMS", es: "SMS integrado" },
      headline: {
        en: "Text your customers. Text your team. Without another app.",
        es: "Mensajea a tus clientes. Mensajea a tu equipo. Sin otra app.",
      },
      body: {
        en: "Redy includes SMS built on Twilio with two flows: transactional messages to customers (order confirmed, ready for pickup, receipt) and messages to your team (each employee gets their shifts when you publish the schedule; you get low-stock and time-clock alerts). No separate vendor, no extra contract. Marketing texts to customers are a separate add-on.",
        es: "Redy incluye SMS sobre Twilio con dos flujos: mensajes transaccionales al cliente (orden confirmada, lista para pickup, recibo) y mensajes a tu equipo (cada empleado recibe sus turnos cuando publicas el horario; tú recibes alertas de stock bajo y del reloj checador). Sin vendor adicional, sin contrato extra. Los SMS de marketing a clientes son un add-on aparte.",
      },
      highlights: [
        { en: "Customer order ready notifications (transactional)", es: "Notificaciones de orden lista (transaccional)" },
        { en: "Staff texts: each employee gets their shifts when you publish", es: "SMS al staff: cada empleado recibe sus turnos al publicar" },
        { en: "Pre-built templates, edit your tone", es: "Templates preconstruidos, edita el tono" },
        { en: "Compliance: opt-in flow + STOP keyword", es: "Compliance: flujo de opt-in + keyword STOP" },
        { en: "Order texts included in every plan", es: "SMS de pedidos incluidos en todos los planes" },
      ],
    },
    page: {
      metaTitle: {
        en: "SMS for Customers and Staff — Built-In | Redy POS",
        es: "SMS para Clientes y Staff — Integrado | Redy POS",
      },
      metaDescription: {
        en: "Send order-ready texts to customers and shift alerts to your staff — all from the POS. Built on Twilio, with opt-in and STOP handling.",
        es: "Manda SMS de 'orden lista' a clientes y alertas de turno a tu staff — todo desde el POS. Sobre Twilio, con opt-in y manejo de STOP.",
      },
      heroSubhead: {
        en: "Your POS already has every customer's phone. Your POS already has every employee's phone. Why are you using WhatsApp?",
        es: "Tu POS ya tiene el teléfono de cada cliente. Tu POS ya tiene el teléfono de cada empleado. ¿Por qué usas WhatsApp?",
      },
      whatYouGet: [
        { en: "Order confirmation + ready-for-pickup SMS", es: "SMS de confirmación + listo para recoger" },
        { en: "Custom message templates in EN and ES", es: "Templates custom en EN y ES" },
        { en: "Low-stock and time-clock alerts to your phone", es: "Alertas de stock bajo y del reloj checador a tu teléfono" },
        { en: "Bulk shift reminders the night before", es: "Recordatorios masivos del turno la noche previa" },
        { en: "Built-in STOP keyword + opt-in compliance", es: "Keyword STOP + compliance de opt-in" },
      ],
      execution: [
        {
          kicker: { en: "Customer", es: "Cliente" },
          title: { en: "Capture phone at checkout", es: "Capturas el teléfono al cobrar" },
          body: {
            en: "Optional phone-number prompt on the POS or kiosk. Customer types it, hits send, they're opted in.",
            es: "Prompt opcional de teléfono en el POS o kiosko. El cliente lo escribe, lo manda, queda opted-in.",
          },
        },
        {
          kicker: { en: "Customer", es: "Cliente" },
          title: { en: "Auto-text when ready", es: "Auto-SMS cuando está lista" },
          body: {
            en: "When the KDS bumps their ticket as done, the SMS fires automatically — with their order number and any pickup instructions.",
            es: "Cuando el KDS bumpea el ticket como listo, el SMS sale automáticamente — con número de orden e instrucciones de pickup.",
          },
        },
        {
          kicker: { en: "Staff", es: "Staff" },
          title: { en: "Publish the schedule", es: "Publicas el horario" },
          body: {
            en: "Build the week and hit Publish. Each employee with a phone on file gets a text with their shifts.",
            es: "Armas la semana y le das Publicar. Cada empleado con teléfono registrado recibe un SMS con sus turnos.",
          },
        },
        {
          kicker: { en: "Staff", es: "Staff" },
          title: { en: "Alerts that find you", es: "Alertas que te encuentran" },
          body: {
            en: "Running low on an ingredient, or a clock-in that looks off? Redy texts the alert phone you choose. You can turn each alert off in Settings → Notifications.",
            es: "¿Se acaba un ingrediente o un marcaje se ve raro? Redy le manda la alerta al teléfono que elijas. Cada alerta se apaga en Configuración → Notificaciones.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/sms-customers-staff/01-customer.png",
          alt: { en: "Customer SMS notification on phone", es: "SMS al cliente en su teléfono" },
          caption: { en: "Customer's phone — 'Your order is ready'.", es: "Teléfono del cliente — 'Tu orden está lista'." },
        },
        {
          src: "/marketing/features/sms-customers-staff/02-staff.png",
          alt: { en: "Schedule published by text", es: "Horario publicado por SMS" },
          caption: { en: "Publish once, everyone gets their shifts.", es: "Publicas una vez, todos reciben sus turnos." },
        },
        {
          src: "/marketing/features/sms-customers-staff/03-templates.png",
          alt: { en: "SMS template editor", es: "Editor de templates SMS" },
          caption: { en: "Edit templates in EN and ES.", es: "Edita templates en EN y ES." },
        },
      ],
    },
  },
  {
    slug: "inventory-cogs",
    vertical: "restaurant",
    icon: Package,
    card: {
      title: { en: "Inventory & COGS", es: "Inventario y COGS" },
      desc: {
        en: "Recipes, stock levels, low-stock alerts and real cost-of-goods. Know exactly what's bleeding margin.",
        es: "Recetas, stock, alertas de bajo inventario y COGS real. Sabes exactamente qué te está sangrando margen.",
      },
    },
    modal: {
      kicker: { en: "Inventory & food cost", es: "Inventario y costo" },
      headline: {
        en: "Know what's eating your margin. Before it eats you.",
        es: "Sabe qué te está comiendo margen. Antes de que te coma.",
      },
      body: {
        en: "Every recipe maps to ingredients. Every sale decrements stock. Low-stock alerts ping you when something's about to run out. The COGS report tells you the true food cost of every item — not what you wish it was, what it actually is.",
        es: "Cada receta mapea a ingredientes. Cada venta descuenta stock. Las alertas de bajo inventario te avisan antes de que se acabe. El reporte de COGS te da el costo real de cada item — no lo que crees, lo que realmente es.",
      },
      highlights: [
        { en: "Recipes with portion-level ingredient mapping", es: "Recetas con mapeo de ingredientes por porción" },
        { en: "Real-time stock decrement on every sale", es: "Descuento de stock en cada venta" },
        { en: "Low-stock alerts by SMS", es: "Alertas de bajo stock por SMS" },
        { en: "Cycle count and variance report", es: "Conteo cíclico y reporte de variance" },
        { en: "True COGS per item and category", es: "COGS real por item y categoría" },
      ],
    },
    page: {
      metaTitle: {
        en: "Inventory, Recipes & COGS — Redy POS",
        es: "Inventario, Recetas y COGS — Redy POS",
      },
      metaDescription: {
        en: "Track recipes, stock levels, variance and true cost-of-goods directly from your POS. Get low-stock alerts. Stop guessing at food cost.",
        es: "Trackea recetas, stock, variance y COGS real directamente desde tu POS. Alertas de bajo stock. Deja de adivinar el costo.",
      },
      heroSubhead: {
        en: "If you don't know the true cost of your bestseller, you don't know if your bestseller is making you money.",
        es: "Si no sabes el costo real de tu bestseller, no sabes si tu bestseller te está dejando dinero.",
      },
      whatYouGet: [
        { en: "Ingredient master list with units and supplier", es: "Master de ingredientes con unidades y supplier" },
        { en: "Recipe builder per menu item (or variant)", es: "Recetas por item de menú (o variante)" },
        { en: "Live stock levels updated by every sale", es: "Stock en vivo actualizado por cada venta" },
        { en: "Cycle count workflow (full or spot)", es: "Workflow de conteo cíclico (completo o spot)" },
        { en: "Purchase orders to suppliers", es: "Órdenes de compra a suppliers" },
        { en: "Variance + waste tracking", es: "Tracking de variance + merma" },
      ],
      execution: [
        {
          kicker: { en: "Setup", es: "Setup" },
          title: { en: "List your ingredients", es: "Listas tus ingredientes" },
          body: {
            en: "Admin → Inventory → Ingredients. Add each ingredient with its unit (oz, lb, count), case size and supplier.",
            es: "Admin → Inventario → Ingredientes. Agrega cada ingrediente con unidad (oz, lb, count), tamaño de caja y supplier.",
          },
        },
        {
          kicker: { en: "Setup", es: "Setup" },
          title: { en: "Map recipes", es: "Mapeas recetas" },
          body: {
            en: "For each menu item, drag in the ingredients and quantities. Burger = 1 patty + 1 bun + 0.5 oz onion. Done.",
            es: "Para cada item del menú, arrastras ingredientes y cantidades. Hamburguesa = 1 patty + 1 pan + 0.5 oz cebolla. Listo.",
          },
        },
        {
          kicker: { en: "Service", es: "Servicio" },
          title: { en: "Sell and watch stock fall", es: "Vendes y el stock baja" },
          body: {
            en: "Every sale decrements ingredients in real time. When patty count crosses your reorder threshold, you get an alert.",
            es: "Cada venta descuenta ingredientes en vivo. Cuando los patties bajan del umbral, recibes alerta.",
          },
        },
        {
          kicker: { en: "End of week", es: "Fin de semana" },
          title: { en: "Read COGS + variance", es: "Lees COGS + variance" },
          body: {
            en: "The report shows your true food cost % per item and category, plus variance between theoretical and actual stock. Hunt the leaks.",
            es: "El reporte te da costo real por item y categoría, más variance entre stock teórico y real. Cazas las fugas.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/inventory-cogs/01-recipe.png",
          alt: { en: "Recipe builder mapping ingredients", es: "Constructor de receta mapeando ingredientes" },
          caption: { en: "Build a recipe in 30 seconds.", es: "Armas una receta en 30 segundos." },
        },
        {
          src: "/marketing/features/inventory-cogs/02-stock.png",
          alt: { en: "Live stock level dashboard", es: "Dashboard de stock en vivo" },
          caption: { en: "Stock falls with every sale.", es: "El stock baja con cada venta." },
        },
        {
          src: "/marketing/features/inventory-cogs/03-cogs.png",
          alt: { en: "COGS and variance report", es: "Reporte de COGS y variance" },
          caption: { en: "True food cost — by item.", es: "Costo real — por item." },
        },
      ],
    },
  },
  {
    slug: "loyalty-crm",
    vertical: "restaurant",
    icon: Heart,
    card: {
      title: { en: "Loyalty & CRM", es: "Lealtad y CRM" },
      desc: {
        en: "Capture the customer's phone, give them a point, bring them back. Your customer list stays yours.",
        es: "Captura el teléfono del cliente, dale un punto, tráelo de vuelta. Tu lista de clientes sigue siendo tuya.",
      },
    },
    modal: {
      kicker: { en: "Customer loyalty", es: "Lealtad del cliente" },
      headline: {
        en: "Your customer list should belong to you.",
        es: "Tu lista de clientes debería ser tuya.",
      },
      body: {
        en: "Every time a customer pays, capture their phone (optional). Every dollar earns points. After enough points, they get a reward you defined. Their visits and spend build up in one profile you can search and message, with their opt-in.",
        es: "Cada vez que un cliente paga, capturas su teléfono (opcional). Cada dólar suma puntos. Con suficientes puntos, reciben un reward que tú defines. Sus visitas y su gasto quedan en un perfil que puedes buscar y al que puedes escribirle, con su permiso.",
      },
      highlights: [
        { en: "Points per dollar spent", es: "Puntos por dólar gastado" },
        { en: "Rewards: free item, $ off, percentage off", es: "Rewards: item gratis, $ off, % off" },
        { en: "Customer profile with visit history + spend", es: "Perfil del cliente con historial + spend" },
        { en: "Search by phone, name or email", es: "Búsqueda por teléfono, nombre o email" },
        { en: "Full contact details on Pro", es: "Datos de contacto completos en Pro" },
      ],
    },
    page: {
      metaTitle: {
        en: "Loyalty Program + Customer CRM — Redy POS",
        es: "Programa de Lealtad + CRM — Redy POS",
      },
      metaDescription: {
        en: "Capture phone + visit history at the POS. Run a points-based loyalty program. Bring your regulars back.",
        es: "Captura teléfono e historial al cobrar. Programa de lealtad por puntos. Trae de vuelta a tus clientes de siempre.",
      },
      heroSubhead: {
        en: "Your regulars are the most valuable list you have. It should be yours.",
        es: "Tus clientes de siempre son la lista más valiosa que tienes. Debería ser tuya.",
      },
      whatYouGet: [
        { en: "Phone or email capture at checkout (optional)", es: "Captura de teléfono o email al cobrar (opcional)" },
        { en: "Configurable points per dollar", es: "Puntos por dólar — configurable" },
        { en: "Define your rewards and thresholds", es: "Defines tus rewards y thresholds" },
        { en: "Customer dashboard with visits and lifetime spend", es: "Dashboard del cliente con visitas y spend total" },
        { en: "SMS opt-in tracked per customer", es: "Opt-in de SMS registrado por cliente" },
      ],
      execution: [
        {
          kicker: { en: "Setup", es: "Setup" },
          title: { en: "Define your program", es: "Defines tu programa" },
          body: {
            en: "Admin → Loyalty. Set points per dollar and the reward (e.g. 100 points = free coffee). Optional welcome reward on signup.",
            es: "Admin → Lealtad. Defines puntos por dólar y el reward (ej. 100 puntos = café gratis). Welcome reward opcional al signup.",
          },
        },
        {
          kicker: { en: "Capture", es: "Captura" },
          title: { en: "Phone at checkout", es: "Teléfono al cobrar" },
          body: {
            en: "Cashier prompts: 'Phone for points?' Customer types it, hits Done. They're in your CRM, earning their first point.",
            es: "El cajero pregunta: '¿Teléfono para puntos?' El cliente lo escribe, presiona Listo. Ya está en tu CRM, ganando su primer punto.",
          },
        },
        {
          kicker: { en: "Reward", es: "Premio" },
          title: { en: "POS reminds them", es: "El POS les recuerda" },
          body: {
            en: "On a return visit, when they give their phone, the POS shows their points and whether a reward is available, e.g. 'Reward available: free coffee'.",
            es: "En una visita siguiente, cuando dan su teléfono, el POS muestra sus puntos y si tienen un reward disponible, ej. 'Reward disponible: café gratis'.",
          },
        },
        {
          kicker: { en: "Grow", es: "Creces" },
          title: { en: "Use your list", es: "Usas tu lista" },
          body: {
            en: "From Admin → Customers, see your top customers with visits and lifetime spend. SMS campaigns to opted-in customers are an add-on. Your list is a real asset now.",
            es: "En Admin → Clientes, ves a tus top clientes con visitas y gasto total. Las campañas SMS a clientes con opt-in son un add-on. Tu lista ya es un activo real.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/loyalty-crm/01-capture.png",
          alt: { en: "Phone capture at POS checkout", es: "Captura de teléfono al cobrar" },
          caption: { en: "Capture in 5 seconds at checkout.", es: "Captura en 5 segundos al cobrar." },
        },
        {
          src: "/marketing/features/loyalty-crm/02-customer.png",
          alt: { en: "Customer profile with visit history", es: "Perfil del cliente con historial" },
          caption: { en: "Customer profile + visit history.", es: "Perfil del cliente e historial." },
        },
        {
          src: "/marketing/features/loyalty-crm/03-list.png",
          alt: { en: "Customer list", es: "Lista de clientes" },
          caption: { en: "Your regulars, in one place.", es: "Tus clientes de siempre, en un solo lugar." },
        },
      ],
    },
  },
  {
    slug: "works-offline",
    vertical: "restaurant",
    icon: WifiOff,
    card: {
      title: { en: "Works Offline", es: "Funciona Sin Internet" },
      desc: {
        en: "Keep ringing cash sales when WiFi drops. They sync the moment you reconnect.",
        es: "Sigue cobrando en efectivo si el WiFi cae. Se sincroniza al reconectar.",
      },
    },
    modal: {
      kicker: { en: "Offline cash mode", es: "Modo efectivo sin internet" },
      headline: {
        en: "WiFi drops at 7 PM. Your cash line keeps moving.",
        es: "El WiFi se cae a las 7 PM. Tu fila de efectivo sigue avanzando.",
      },
      body: {
        en: "If your connection drops, the POS switches to cash mode: you keep ringing cash sales and they're saved on the device. When the WiFi comes back, they sync on their own, and any sale that can't sync is flagged for review. Card payments, tabs and online orders need a connection.",
        es: "Si la conexión se cae, el POS pasa a modo efectivo: sigues cobrando en efectivo y las ventas se guardan en el dispositivo. Cuando vuelve el WiFi, se sincronizan solas, y cualquier venta que no pueda sincronizar queda marcada para revisión. Los pagos con tarjeta, las tabs y los pedidos online necesitan conexión.",
      },
      highlights: [
        { en: "Cash sales without WiFi, saved on the device", es: "Ventas en efectivo sin WiFi, guardadas en el dispositivo" },
        { en: "Automatic sync when you reconnect", es: "Sync automático al reconectar" },
        { en: "KDS keeps showing the tickets it already has", es: "El KDS sigue mostrando los tickets que ya tenía" },
        { en: "Auto-detect + visual indicator when offline", es: "Auto-detección + indicador visual offline" },
        { en: "Cards need a connection — the POS tells staff to take cash", es: "La tarjeta necesita conexión — el POS le indica al staff cobrar en efectivo" },
      ],
    },
    page: {
      metaTitle: {
        en: "Offline Cash Mode — Keep Selling When WiFi Drops | Redy",
        es: "Modo Efectivo Sin Internet — Sigue Vendiendo si Cae el WiFi | Redy",
      },
      metaDescription: {
        en: "When WiFi drops, Redy keeps ringing cash sales and saves them on the device. They sync the moment you reconnect. Cards need a connection.",
        es: "Si cae el WiFi, Redy sigue cobrando en efectivo y guarda las ventas en el dispositivo. Se sincronizan al reconectar. La tarjeta necesita conexión.",
      },
      heroSubhead: {
        en: "A dropped connection shouldn't stop the cash line.",
        es: "Una conexión caída no debería frenar la fila de efectivo.",
      },
      whatYouGet: [
        { en: "Ring cash sales offline, with discounts and notes", es: "Cobros en efectivo offline, con descuentos y notas" },
        { en: "KDS shows its connection state; new tickets arrive on reconnect", es: "El KDS muestra su estado de conexión; los tickets nuevos llegan al reconectar" },
        { en: "Cash orders complete normally, sync on reconnect", es: "Órdenes en efectivo completan normal, sync al reconectar" },
        { en: "Card, tabs and online orders resume when you reconnect", es: "Tarjeta, tabs y pedidos online vuelven al reconectar" },
        { en: "Visual banner so staff knows the state", es: "Banner visual para que staff vea el estado" },
        { en: "Sales that fail to sync are flagged for review", es: "Las ventas que no sincronizan quedan marcadas para revisión" },
      ],
      execution: [
        {
          kicker: { en: "Detection", es: "Detección" },
          title: { en: "It just notices", es: "Lo detecta solo" },
          body: {
            en: "Redy checks the connection continuously. The moment you go offline, a banner appears: 'Offline mode — cash only.' Staff knows what to do.",
            es: "Redy revisa la conexión todo el tiempo. En cuanto la pierdes, aparece un banner: 'Modo offline — solo efectivo.' El staff sabe qué hacer.",
          },
        },
        {
          kicker: { en: "Service", es: "Servicio" },
          title: { en: "Keep working", es: "Sigues trabajando" },
          body: {
            en: "Ring cash sales. Each one is saved on the device. The KDS keeps the tickets it already had on screen; new ones arrive when the connection is back.",
            es: "Cobras en efectivo. Cada venta se guarda en el dispositivo. El KDS mantiene en pantalla los tickets que ya tenía; los nuevos llegan cuando vuelve la conexión.",
          },
        },
        {
          kicker: { en: "Card payments", es: "Pagos con tarjeta" },
          title: { en: "Cards wait for the connection", es: "La tarjeta espera la conexión" },
          body: {
            en: "Card payments need a connection to be authorized, so Redy doesn't try them offline. The POS tells staff to take cash until the connection is back.",
            es: "Los pagos con tarjeta necesitan conexión para autorizarse, así que Redy no los intenta offline. El POS le indica al staff cobrar en efectivo hasta que vuelva la conexión.",
          },
        },
        {
          kicker: { en: "Reconnect", es: "Reconexión" },
          title: { en: "Everything catches up", es: "Todo se pone al día" },
          body: {
            en: "WiFi comes back. The cash sales saved on the device sync to your reports. If one can't sync, the POS flags it for review instead of losing it.",
            es: "Vuelve el WiFi. Las ventas en efectivo guardadas en el dispositivo se sincronizan a tus reportes. Si alguna no puede, el POS la marca para revisión en vez de perderla.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/works-offline/01-banner.png",
          alt: { en: "Offline banner on POS", es: "Banner de offline en el POS" },
          caption: { en: "Offline banner — staff knows the state.", es: "Banner offline — staff sabe el estado." },
        },
        {
          src: "/marketing/features/works-offline/02-queue.png",
          alt: { en: "Cash sales waiting to sync", es: "Ventas en efectivo esperando sync" },
          caption: { en: "Cash sales — saved on the device.", es: "Ventas en efectivo — guardadas en el dispositivo." },
        },
        {
          src: "/marketing/features/works-offline/03-sync.png",
          alt: { en: "Sales synced after reconnecting", es: "Ventas sincronizadas al reconectar" },
          caption: { en: "Back online — sales synced.", es: "De vuelta online — ventas sincronizadas." },
        },
      ],
    },
  },

  // ───────────────────────────────────────────────────────────────────────────
  // NIGHTLIFE FEATURES
  // ───────────────────────────────────────────────────────────────────────────

  {
    slug: "event-ticketing",
    vertical: "nightlife",
    icon: Ticket,
    accent: "#C4B5FD",
    card: {
      title: { en: "Event Ticketing", es: "Venta de Tickets" },
      desc: {
        en: "Public checkout page with your branding. Unlimited ticket tiers, presale pricing, capacity caps, and a setup wizard that gets you live in under 10 minutes.",
        es: "Página de venta pública con tu branding. Tiers ilimitados, presale, control de aforo y un wizard que te lanza en menos de 10 minutos.",
      },
    },
    modal: {
      kicker: { en: "Ticketing platform", es: "Plataforma de tickets" },
      headline: {
        en: "Run your own ticketing. Pay 0% platform fee.",
        es: "Corre tu propia venta de tickets. Paga 0% de fee de plataforma.",
      },
      body: {
        en: "Build an event in under 10 minutes with the setup wizard. Public branded checkout page. Unlimited tiers (GA, VIP, table reservation, presale). Capacity caps with auto-close. Apple Pay, Google Pay, card. The buyer's email and ticket QR are yours.",
        es: "Construye un evento en menos de 10 minutos con el wizard. Página de venta pública brandeada. Tiers ilimitados (GA, VIP, mesa, presale). Control de aforo con auto-cierre. Apple Pay, Google Pay, tarjeta. El email y QR del comprador son tuyos.",
      },
      highlights: [
        { en: "Event setup wizard with go-live checklist", es: "Wizard de evento con checklist de go-live" },
        { en: "Unlimited tiers + presale + early bird", es: "Tiers ilimitados + presale + early bird" },
        { en: "Capacity caps with auto-sold-out", es: "Aforo con auto-sold-out" },
        { en: "Apple Pay + Google Pay + card", es: "Apple Pay + Google Pay + tarjeta" },
        { en: "QR ticket emailed to buyer instantly", es: "Ticket QR al email del comprador al instante" },
      ],
    },
    page: {
      metaTitle: {
        en: "Event Ticketing with 0% Platform Fee — Redy Nightlife",
        es: "Venta de Tickets con 0% de Fee — Redy Nightlife",
      },
      metaDescription: {
        en: "Sell event tickets from your branded checkout page. Unlimited tiers, presale, capacity caps, Apple Pay. 0% platform fee. Go live in under 10 minutes.",
        es: "Vende tickets desde tu propia página brandeada. Tiers ilimitados, presale, aforo, Apple Pay. 0% de fee. En vivo en menos de 10 minutos.",
      },
      heroSubhead: {
        en: "Ticketing platforms usually add a fee to every ticket. Redy charges no platform fee per ticket — your plan covers it.",
        es: "Las plataformas de tickets suelen sumar un fee a cada entrada. Redy no cobra fee de plataforma por ticket — lo cubre tu plan.",
      },
      whatYouGet: [
        { en: "Event creation wizard (under 10 minutes)", es: "Wizard de evento (menos de 10 minutos)" },
        { en: "Unlimited tiers, presale, early bird, VIP", es: "Tiers ilimitados, presale, early bird, VIP" },
        { en: "Capacity per tier + total event cap", es: "Aforo por tier + aforo total del evento" },
        { en: "Branded checkout page on your domain", es: "Checkout brandeado en tu dominio" },
        { en: "Buyer database: email, phone, ticket history", es: "Base de compradores: email, teléfono, historial" },
        { en: "Live sales dashboard with realtime counter", es: "Dashboard de ventas con contador en vivo" },
      ],
      execution: [
        {
          kicker: { en: "Step 1", es: "Paso 1" },
          title: { en: "Open the wizard", es: "Abres el wizard" },
          body: {
            en: "Admin → Events → New. Name, date, venue, hero image. Define your tiers and prices.",
            es: "Admin → Eventos → Nuevo. Nombre, fecha, venue, imagen hero. Defines tiers y precios.",
          },
        },
        {
          kicker: { en: "Step 2", es: "Paso 2" },
          title: { en: "Run the checklist", es: "Corres el checklist" },
          body: {
            en: "Setup checklist verifies you have payments enabled, an ID-check policy, and a confirmation message. Green ticks = ready to launch.",
            es: "El checklist verifica que tienes pagos activos, política de ID y mensaje de confirmación. Ticks verdes = listo para lanzar.",
          },
        },
        {
          kicker: { en: "Step 3", es: "Paso 3" },
          title: { en: "Hit Go Live", es: "Le das Go Live" },
          body: {
            en: "Your public sales page is live. Share the link on socials, send to promoters, embed on your venue site.",
            es: "Tu página pública está viva. Comparte el link en socials, mándalo a promotores, embed en tu sitio del venue.",
          },
        },
        {
          kicker: { en: "Step 4", es: "Paso 4" },
          title: { en: "Watch sales roll in", es: "Ves entrar las ventas" },
          body: {
            en: "Live dashboard counts tickets sold per tier in real time. Capacity hits cap? The tier auto-closes and shows 'Sold Out'.",
            es: "El dashboard cuenta tickets por tier en vivo. ¿Llegas al aforo? El tier se auto-cierra y muestra 'Sold Out'.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/event-ticketing/01-wizard.png",
          alt: { en: "Event creation wizard", es: "Wizard de creación de evento" },
          caption: { en: "Wizard guides you in under 10 min.", es: "El wizard te guía en menos de 10 min." },
        },
        {
          src: "/marketing/features/event-ticketing/02-checkout.png",
          alt: { en: "Public ticket checkout page", es: "Página pública de checkout" },
          caption: { en: "Your branded public checkout.", es: "Tu checkout público brandeado." },
        },
        {
          src: "/marketing/features/event-ticketing/03-dashboard.png",
          alt: { en: "Live event sales dashboard", es: "Dashboard de ventas en vivo" },
          caption: { en: "Live sales counter, per tier.", es: "Contador de ventas en vivo, por tier." },
        },
      ],
    },
  },
  {
    slug: "qr-door-check-in",
    vertical: "nightlife",
    icon: QrCode,
    accent: "#F472B6",
    card: {
      title: { en: "QR Door Check-in", es: "Check-in QR en la Puerta" },
      desc: {
        en: "Scan tickets fast. Duplicate detection, a log of every entry, and a live capacity count. The door needs WiFi or mobile data.",
        es: "Escanea tickets rápido. Detección de duplicados, log de cada entrada y aforo en vivo. La puerta necesita WiFi o datos móviles.",
      },
    },
    modal: {
      kicker: { en: "Door check-in", es: "Check-in en puerta" },
      headline: {
        en: "Scan a ticket. Catch every duplicate.",
        es: "Escanea un ticket. Detecta cada duplicado.",
      },
      body: {
        en: "The door staff opens the door screen on any phone or iPad and points the camera at the QR. Each scan is checked against the event's tickets. Used ticket? Big red X with the timestamp it was first scanned. The audit log records who scanned what and when. If the signal drops, scanning pauses and resumes on its own when it returns.",
        es: "El staff de puerta abre la pantalla de puerta en cualquier teléfono o iPad y apunta la cámara al QR. Cada escaneo se valida contra los tickets del evento. ¿Ticket usado? X grande roja con el timestamp del primer escaneo. El audit log registra quién escaneó qué y cuándo. Si se cae la señal, el escaneo se pausa y vuelve solo cuando regresa.",
      },
      highlights: [
        { en: "Works over WiFi or mobile data", es: "Funciona con WiFi o datos móviles" },
        { en: "Scanning pauses and resumes on its own if the signal drops", es: "El escaneo se pausa y vuelve solo si se cae la señal" },
        { en: "Duplicate detection with timestamp", es: "Detección de duplicados con timestamp" },
        { en: "Audit log per scanner + per door", es: "Audit log por scanner y por puerta" },
        { en: "Realtime count of guests inside", es: "Conteo en vivo de invitados adentro" },
      ],
    },
    page: {
      metaTitle: {
        en: "QR Door Check-in with Duplicate Detection — Redy Nightlife",
        es: "Check-in QR con Detección de Duplicados — Redy Nightlife",
      },
      metaDescription: {
        en: "Scan event tickets fast. Duplicate detection, audit log, live capacity counter. Built for nightclubs.",
        es: "Escanea tickets rápido. Detección de duplicados, audit log, contador de aforo en vivo. Hecho para nightclubs.",
      },
      heroSubhead: {
        en: "The line moves at the speed of your scanners.",
        es: "La fila se mueve a la velocidad de tus scanners.",
      },
      whatYouGet: [
        { en: "Door app runs on any iOS/Android phone or tablet", es: "App de puerta corre en cualquier iOS/Android" },
        { en: "Clear connection indicator at the door", es: "Indicador de conexión claro en la puerta" },
        { en: "Fast scan validation", es: "Validación de scan rápida" },
        { en: "Duplicate scan = clear red flag with first-scan timestamp", es: "Duplicado = flag rojo con timestamp del primer scan" },
        { en: "Live capacity counter — know when you're at 80%", es: "Contador de aforo en vivo — sabes cuándo estás al 80%" },
        { en: "Audit log: who scanned, where, when", es: "Audit log: quién escaneó, dónde, cuándo" },
      ],
      execution: [
        {
          kicker: { en: "Pre-doors", es: "Antes de abrir" },
          title: { en: "Check the signal", es: "Revisas la señal" },
          body: {
            en: "Each door device opens the door screen and picks the event. Check the signal at the entrance before doors open — use mobile data or a repeater if WiFi is weak there.",
            es: "Cada dispositivo de puerta abre la pantalla de puerta y elige el evento. Revisa la señal en la entrada antes de abrir — usa datos móviles o un repetidor si el WiFi es débil ahí.",
          },
        },
        {
          kicker: { en: "Doors open", es: "Abren puertas" },
          title: { en: "Point and scan", es: "Apuntas y escaneas" },
          body: {
            en: "Camera reads the QR. Green check + name + tier = let them in. Red X + first-scan timestamp = stop.",
            es: "La cámara lee el QR. Check verde + nombre + tier = entran. X roja + timestamp del primer escaneo = stop.",
          },
        },
        {
          kicker: { en: "Inside", es: "Adentro" },
          title: { en: "Live counter ticks up", es: "Contador en vivo sube" },
          body: {
            en: "Each successful scan adds to the live capacity counter. Supervisor sees the count from the admin in real time.",
            es: "Cada escaneo exitoso suma al contador. El supervisor ve el conteo desde el admin en vivo.",
          },
        },
        {
          kicker: { en: "After", es: "Después" },
          title: { en: "Read the audit log", es: "Lees el audit log" },
          body: {
            en: "Every scan is logged with scanner, ticket ID and timestamp, so end-of-night reconciliation is quick.",
            es: "Cada escaneo queda registrado con scanner, ticket ID y timestamp, así la reconciliación de cierre es rápida.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/qr-door-check-in/01-scan.png",
          alt: { en: "Door scanner showing successful check-in", es: "Scanner de puerta mostrando check-in exitoso" },
          caption: { en: "Fast scan, clear result.", es: "Escaneo rápido, resultado claro." },
        },
        {
          src: "/marketing/features/qr-door-check-in/02-duplicate.png",
          alt: { en: "Duplicate ticket detection", es: "Detección de ticket duplicado" },
          caption: { en: "Used ticket — caught instantly.", es: "Ticket usado — detectado al instante." },
        },
        {
          src: "/marketing/features/qr-door-check-in/03-counter.png",
          alt: { en: "Live capacity counter on admin", es: "Contador de aforo en vivo en admin" },
          caption: { en: "Live capacity — know when you're at 80%.", es: "Aforo en vivo — sabes cuándo estás al 80%." },
        },
      ],
    },
  },
  {
    slug: "live-bar-tabs",
    vertical: "nightlife",
    icon: Beer,
    accent: "#FB923C",
    card: {
      title: { en: "Live Bar Tabs", es: "Tabs de Barra en Vivo" },
      desc: {
        en: "Open tabs by name or VIP table, with realtime sync across every bartender's screen. PIN gate for voids, deep inventory and recipe pours.",
        es: "Abre tabs por nombre o mesa VIP, sincronizados en vivo entre todos los bartenders. PIN gate para voids, inventario y recetas con onzas.",
      },
    },
    modal: {
      kicker: { en: "Bar terminal", es: "Terminal de barra" },
      headline: {
        en: "One tab. Five bartenders. Zero confusion.",
        es: "Una tab. Cinco bartenders. Cero confusión.",
      },
      body: {
        en: "Open a tab by name or VIP table. Every bartender sees every tab in real time. Add items from any station. Send to one tab from any bar. PIN-protected voids and comps. Recipe-level inventory tracks every pour automatically.",
        es: "Abres tab por nombre o mesa VIP. Cada bartender ve cada tab en vivo. Agregas items desde cualquier estación. Despachas a una tab desde cualquier barra. Voids y comps protegidos con PIN. El inventario por receta descuenta cada onza automáticamente.",
      },
      highlights: [
        { en: "Tabs by name or VIP table", es: "Tabs por nombre o mesa VIP" },
        { en: "Realtime sync across every bar station", es: "Sync en vivo entre todas las estaciones" },
        { en: "Recipe-level pours track inventory automatically", es: "Onzas por receta trackean inventario automático" },
        { en: "PIN gate for voids, comps and tab transfers", es: "PIN gate para voids, comps y transfer de tabs" },
        { en: "Close a tab by card, cash or split bill", es: "Cierra tab con tarjeta, efectivo o split" },
      ],
    },
    page: {
      metaTitle: {
        en: "Live Bar Tabs with Realtime Sync — Redy Nightlife",
        es: "Tabs de Barra en Vivo con Sync — Redy Nightlife",
      },
      metaDescription: {
        en: "Open tabs by name or VIP table. Every bartender sees every tab in real time. Recipe inventory, PIN voids, multi-station fire.",
        es: "Abre tabs por nombre o mesa VIP. Cada bartender ve cada tab en vivo. Inventario por receta, voids con PIN, fire multi-estación.",
      },
      heroSubhead: {
        en: "A lot of bars still run on a paper tab book. Yours doesn't have to.",
        es: "Muchos bares todavía corren con tabs en libreta de papel. El tuyo no tiene que hacerlo.",
      },
      whatYouGet: [
        { en: "Open by name or VIP table", es: "Abrir por nombre o mesa VIP" },
        { en: "Realtime tab list visible from every bar terminal", es: "Lista de tabs visible en cada terminal de barra" },
        { en: "Recipe-level inventory: each pour decrements stock", es: "Inventario por receta: cada onza descuenta stock" },
        { en: "Server orders drawer (waitstaff fires to bar)", es: "Drawer de órdenes del server (waitstaff dispara a barra)" },
        { en: "Transfer a tab between bartenders / sections", es: "Transferir tab entre bartenders / secciones" },
        { en: "Close with card, cash, or split", es: "Cerrar con tarjeta, efectivo o split" },
      ],
      execution: [
        {
          kicker: { en: "Open", es: "Abres" },
          title: { en: "Tab by name", es: "Tab por nombre" },
          body: {
            en: "Bartender taps 'New Tab' and types the customer's name, or picks their VIP table. The tab appears at every bar station.",
            es: "El bartender da tap a 'Nueva Tab' y escribe el nombre del cliente, o elige su mesa VIP. La tab aparece en todas las estaciones.",
          },
        },
        {
          kicker: { en: "Pour", es: "Despacho" },
          title: { en: "Fire from any station", es: "Dispara desde cualquier estación" },
          body: {
            en: "Any bartender adds items to any tab. The pours decrement liquor inventory by recipe — 1.5 oz vodka, 0.5 oz triple sec, lime.",
            es: "Cualquier bartender agrega a cualquier tab. Las onzas descuentan inventario por receta — 1.5 oz vodka, 0.5 oz triple sec, limón.",
          },
        },
        {
          kicker: { en: "Hand off", es: "Hand off" },
          title: { en: "Transfer or comp", es: "Transferir o comp" },
          body: {
            en: "Customer moves to VIP? Transfer the tab to the bottle service host with a tap. Comp an item? Manager PIN, logged.",
            es: "¿Cliente se mueve a VIP? Transfieres la tab al host de bottle service con un tap. ¿Comp? PIN de manager, queda registrado.",
          },
        },
        {
          kicker: { en: "Close", es: "Cierre" },
          title: { en: "Card, cash, or split", es: "Tarjeta, efectivo o split" },
          body: {
            en: "Charge the card when they close out, take cash, or split between 4 guests. Tip line, done.",
            es: "Cobras la tarjeta al cerrar, recibes efectivo o divides entre 4 personas. Línea de propina, fin.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/live-bar-tabs/01-tabs.png",
          alt: { en: "Active bar tabs list", es: "Lista de tabs activas" },
          caption: { en: "Every tab, visible on every screen.", es: "Cada tab, visible en cada pantalla." },
        },
        {
          src: "/marketing/features/live-bar-tabs/02-pour.png",
          alt: { en: "Bartender adding a drink with recipe", es: "Bartender agregando un drink con receta" },
          caption: { en: "Recipe pours decrement inventory.", es: "Onzas descuentan inventario." },
        },
        {
          src: "/marketing/features/live-bar-tabs/03-close.png",
          alt: { en: "Closing a tab", es: "Cerrando una tab" },
          caption: { en: "Close out in seconds.", es: "Cierras en segundos." },
        },
      ],
    },
  },
  {
    slug: "vip-table-map",
    vertical: "nightlife",
    icon: MapPin,
    accent: "#4A7AE8",
    card: {
      title: { en: "VIP Table Map", es: "Mapa de Mesas VIP" },
      desc: {
        en: "Drag-and-drop your floor plan. Hosts see open vs reserved vs occupied in realtime, supervisor PIN to comp or close. Bottle service in two taps.",
        es: "Tu floor plan drag-and-drop. Los hosts ven abierto vs reservado vs ocupado en vivo, supervisor PIN para comp o cerrar. Bottle service en dos taps.",
      },
    },
    modal: {
      kicker: { en: "VIP & bottle service", es: "VIP y bottle service" },
      headline: {
        en: "Your floor plan. Live. On every host's screen.",
        es: "Tu floor plan. En vivo. En la pantalla de cada host.",
      },
      body: {
        en: "Draw your VIP floor plan once. Every host and supervisor sees it in real time, color-coded by state: open, reserved, occupied, closed. Tap a table to seat guests, fire bottles, transfer a bar tab, run a card. Supervisor PIN gates comps and closes. Bottle service in two taps.",
        es: "Dibujas tu floor plan VIP una vez. Cada host y supervisor lo ve en vivo, codificado por estado: abierto, reservado, ocupado, cerrado. Tap a una mesa para sentar, disparar botellas, transferir tab de barra, cobrar. PIN de supervisor para comps y cierres. Bottle service en dos taps.",
      },
      highlights: [
        { en: "Visual drag-and-drop floor plan builder", es: "Constructor visual drag-and-drop del floor plan" },
        { en: "Realtime state: open / reserved / occupied", es: "Estado en vivo: abierto / reservado / ocupado" },
        { en: "Bottle service workflow in two taps", es: "Bottle service en dos taps" },
        { en: "Supervisor PIN for comps and table closes", es: "PIN de supervisor para comps y cierres" },
        { en: "Reservations with deposit tracking", es: "Reservas con tracking de depósito" },
      ],
    },
    page: {
      metaTitle: {
        en: "VIP Table Map with Realtime Status — Redy Nightlife",
        es: "Mapa de Mesas VIP con Estado en Vivo — Redy Nightlife",
      },
      metaDescription: {
        en: "Drag-and-drop floor plan, realtime status per table, bottle service in two taps, supervisor PIN for comps. Built for nightclubs.",
        es: "Floor plan drag-and-drop, estado en vivo por mesa, bottle service en dos taps, PIN de supervisor. Hecho para nightclubs.",
      },
      heroSubhead: {
        en: "Hosts shouldn't be walking back to the booth to find out which table is still open at 11:30.",
        es: "Los hosts no deberían tener que regresar a la caja para saber qué mesa sigue abierta a las 11:30.",
      },
      whatYouGet: [
        { en: "Drag-and-drop floor plan editor", es: "Editor del floor plan drag-and-drop" },
        { en: "Per-table state, minimum, deposit, host", es: "Estado, mínimo, depósito y host por mesa" },
        { en: "Live updates pushed to every host device", es: "Updates en vivo a cada dispositivo de host" },
        { en: "Bottle service preset menus", es: "Menús preset de bottle service" },
        { en: "Reservation flow with deposit tracking", es: "Flujo de reserva con registro de depósito" },
        { en: "Supervisor PIN required for comp / close", es: "PIN de supervisor para comp y cierre" },
      ],
      execution: [
        {
          kicker: { en: "Setup", es: "Setup" },
          title: { en: "Draw the room", es: "Dibujas el salón" },
          body: {
            en: "Admin → Floor Plan. Drag tables, booths, bottle areas onto the canvas. Set minimums per table.",
            es: "Admin → Floor Plan. Arrastras mesas, booths, áreas de botella al canvas. Configuras mínimos y depósitos por mesa.",
          },
        },
        {
          kicker: { en: "Reservations", es: "Reservas" },
          title: { en: "Record the deposit", es: "Registras el depósito" },
          body: {
            en: "Reservation comes in: pick a table and record the deposit you collected. Table goes 'Reserved' state.",
            es: "Llega reserva: eliges mesa y registras el depósito que cobraste. La mesa pasa a 'Reservada'.",
          },
        },
        {
          kicker: { en: "Doors open", es: "Apertura" },
          title: { en: "Host seats the party", es: "El host sienta al grupo" },
          body: {
            en: "Group arrives. Host taps the table → 'Seat'. Captures guest count and primary contact. Table turns 'Occupied'. Bottle menu opens.",
            es: "Llega el grupo. Host toca la mesa → 'Sentar'. Captura número de personas y contacto. Mesa pasa a 'Ocupada'. Abre menú de botellas.",
          },
        },
        {
          kicker: { en: "Service", es: "Servicio" },
          title: { en: "Bottle service in 2 taps", es: "Bottle service en 2 taps" },
          body: {
            en: "Tap a bottle from the preset menu, fire to bar. Cocktail packages, mixers, sparkler add-ons — all preset. Supervisor PIN to comp.",
            es: "Tap a una botella del menú preset, dispara a barra. Paquetes de cocteles, mixers, sparklers — preset. PIN de supervisor para comp.",
          },
        },
        {
          kicker: { en: "Close", es: "Cierre" },
          title: { en: "Close with PIN", es: "Cierras con PIN" },
          body: {
            en: "End of night, table requests check. Supervisor PIN to close. Tab moves to history with full audit.",
            es: "Cierre, mesa pide cuenta. PIN de supervisor para cerrar. Tab pasa a historia con audit completo.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/vip-table-map/01-floor.png",
          alt: { en: "VIP floor plan with table statuses", es: "Floor plan VIP con estados de mesas" },
          caption: { en: "Floor plan — color-coded states.", es: "Floor plan — estados por color." },
        },
        {
          src: "/marketing/features/vip-table-map/02-bottle.png",
          alt: { en: "Bottle service menu", es: "Menú de bottle service" },
          caption: { en: "Bottle service in two taps.", es: "Bottle service en dos taps." },
        },
        {
          src: "/marketing/features/vip-table-map/03-close.png",
          alt: { en: "Closing VIP table with supervisor PIN", es: "Cerrando mesa VIP con PIN de supervisor" },
          caption: { en: "Supervisor PIN to close.", es: "PIN de supervisor para cerrar." },
        },
      ],
    },
  },
  {
    slug: "promoter-portal",
    vertical: "nightlife",
    icon: Users,
    accent: "#10B981",
    card: {
      title: { en: "Promoter Links & Commissions", es: "Links y Comisiones de Promotores" },
      desc: {
        en: "Each promoter gets their own tracked link. Every sale is attributed and Redy calculates their commission. No spreadsheets.",
        es: "Cada promotor con su propio link rastreado. Cada venta queda atribuida y Redy calcula su comisión. Sin hojas de cálculo.",
      },
    },
    modal: {
      kicker: { en: "Promoter tracking", es: "Tracking de promotores" },
      headline: {
        en: "Promoter links and commissions — calculated for you.",
        es: "Links de promotor y comisiones — calculadas por ti.",
      },
      body: {
        en: "Add a promoter in seconds. They get a tracked link; every ticket sold through it is attributed to them, and Redy calculates the commission — flat per ticket or a percentage. You see sales and commission per promoter, pay them your way, and mark it paid in Redy so the record stays.",
        es: "Agregas un promotor en segundos. Recibe un link rastreado; cada entrada vendida por ahí queda a su nombre y Redy calcula la comisión — fija por entrada o porcentaje. Ves ventas y comisión por promotor, les pagas como prefieras y lo marcas pagado en Redy para que quede el registro.",
      },
      highlights: [
        { en: "Tracked link per promoter (auto-attribution)", es: "Link rastreado por promotor (auto-atribución)" },
        { en: "Sales per promoter, in real time", es: "Ventas por promotor, en vivo" },
        { en: "Flat or percentage commission", es: "Comisión fija o porcentaje" },
        { en: "Mark commissions as paid", es: "Marca las comisiones como pagadas" },
        { en: "Audit trail of every commission earned", es: "Audit trail de cada comisión ganada" },
      ],
    },
    page: {
      metaTitle: {
        en: "Promoter Links & Commission Tracking — Redy Nightlife",
        es: "Links de Promotor y Control de Comisiones — Redy Nightlife",
      },
      metaDescription: {
        en: "Each promoter gets a tracked link. Redy attributes every sale and calculates the commission. No spreadsheets, no guesswork at 4 AM.",
        es: "Cada promotor con link rastreado. Redy atribuye cada venta y calcula la comisión. Sin hojas, sin adivinar a las 4 AM.",
      },
      heroSubhead: {
        en: "Settling promoter commissions at 4 AM from memory is no way to run a business.",
        es: "Cuadrar comisiones de memoria a las 4 AM no es manera de correr un negocio.",
      },
      whatYouGet: [
        { en: "Add a promoter with name, contact and commission", es: "Agregas al promotor con nombre, contacto y comisión" },
        { en: "Tracked link per promoter per event", es: "Link rastreado por promotor por evento" },
        { en: "Live view of tickets and commission per promoter", es: "Vista en vivo de tickets y comisión por promotor" },
        { en: "Flat per ticket or percentage of the ticket", es: "Fija por entrada o porcentaje del ticket" },
        { en: "Mark commissions paid after you pay out", es: "Marcas las comisiones pagadas después de pagar" },
        { en: "Commission history per promoter", es: "Historial de comisiones por promotor" },
      ],
      execution: [
        {
          kicker: { en: "Onboard", es: "Onboarding" },
          title: { en: "Add the promoter", es: "Agregas al promotor" },
          body: {
            en: "Open your event → Promoters → New. Name, contact and commission. Their link is copied instantly — send it by WhatsApp.",
            es: "Abres tu evento → Promotores → Nuevo. Nombre, contacto y comisión. Su link se copia al instante — mándaselo por WhatsApp.",
          },
        },
        {
          kicker: { en: "Sell", es: "Venta" },
          title: { en: "They share their link", es: "Comparten su link" },
          body: {
            en: "Their link auto-attributes every purchase. They share it on socials. You see tickets sold and commission earned per promoter in real time.",
            es: "Su link auto-atribuye cada compra. Lo comparten en socials. Ves tickets vendidos y comisión por promotor en vivo.",
          },
        },
        {
          kicker: { en: "Settle", es: "Settle" },
          title: { en: "Pay and mark paid", es: "Pagas y marcas pagado" },
          body: {
            en: "You pay your promoters your way, then mark their commissions paid in Redy. The record stays.",
            es: "Les pagas a tus promotores como prefieras y marcas sus comisiones como pagadas en Redy. El registro queda.",
          },
        },
        {
          kicker: { en: "Year-end", es: "Fin de año" },
          title: { en: "Keep the record", es: "Queda el registro" },
          body: {
            en: "Every commission earned and paid stays on file per promoter. Your accountant says thank you.",
            es: "Cada comisión ganada y pagada queda registrada por promotor. Tu contador te agradece.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/promoter-portal/01-promoter-dash.png",
          alt: { en: "Sales by promoter", es: "Ventas por promotor" },
          caption: { en: "Sales by promoter, live.", es: "Ventas por promotor, en vivo." },
        },
        {
          src: "/marketing/features/promoter-portal/02-commission.png",
          alt: { en: "Commission rules configuration", es: "Configuración de reglas de comisión" },
          caption: { en: "Flat or percentage commission.", es: "Comisión fija o porcentaje." },
        },
        {
          src: "/marketing/features/promoter-portal/03-payout.png",
          alt: { en: "Commission ledger per promoter", es: "Libro de comisiones por promotor" },
          caption: { en: "Mark commissions paid.", es: "Marca las comisiones pagadas." },
        },
      ],
    },
  },
  {
    slug: "venue-microsite",
    vertical: "nightlife",
    icon: Globe,
    accent: "#A78BFA",
    card: {
      title: { en: "Venue Microsite", es: "Microsite del Venue" },
      desc: {
        en: "Your public website — hero video, gallery, upcoming events feed, private-event inquiry form. Synced to your event calendar in real time.",
        es: "Tu sitio público — video hero, galería, eventos próximos, formulario de eventos privados. Sincronizado a tu calendario en tiempo real.",
      },
    },
    modal: {
      kicker: { en: "Venue website", es: "Sitio del venue" },
      headline: {
        en: "A real venue website. Synced to your event calendar.",
        es: "Un sitio real del venue. Sincronizado al calendario.",
      },
      body: {
        en: "Hero video, photo gallery, upcoming events feed (auto-updated), private event inquiry form, location, social links. The events feed pulls live from your admin — when you publish a new event, it shows up on the site instantly. No webmaster needed.",
        es: "Video hero, galería, feed de eventos próximos (auto-actualizado), formulario de eventos privados, ubicación, links sociales. El feed jala en vivo del admin — cuando publicas evento nuevo, sale al instante. Sin webmaster.",
      },
      highlights: [
        { en: "Hero video or rotating hero image", es: "Video hero o imagen hero rotativa" },
        { en: "Upcoming events feed (auto-updated)", es: "Feed de eventos próximos (auto-actualizado)" },
        { en: "Private event inquiry form (lands in admin)", es: "Formulario de eventos privados (cae en admin)" },
        { en: "Gallery, location, social links", es: "Galería, ubicación, links sociales" },
        { en: "Custom domain on Pro", es: "Dominio personalizado con Pro" },
      ],
    },
    page: {
      metaTitle: {
        en: "Venue Microsite with Live Event Feed — Redy Nightlife",
        es: "Microsite del Venue con Feed en Vivo — Redy Nightlife",
      },
      metaDescription: {
        en: "Your venue's public website. Hero video, gallery, upcoming events synced from your event calendar, private inquiry form. Custom domain on Pro.",
        es: "Sitio público del venue. Video hero, galería, eventos sincronizados del calendario, formulario de inquiries privados. Dominio en Pro.",
      },
      heroSubhead: {
        en: "Don't pay a designer for a venue website that just lists three events and your Instagram link.",
        es: "No le pagues a un diseñador por un sitio del venue que solo lista tres eventos y tu Instagram.",
      },
      whatYouGet: [
        { en: "Hero video / rotating image with text overlay", es: "Video hero / imagen rotativa con texto" },
        { en: "Upcoming events feed pulled live from admin", es: "Feed de eventos próximos en vivo del admin" },
        { en: "Photo gallery (drag-and-drop upload)", es: "Galería (upload drag-and-drop)" },
        { en: "Private event inquiry form (lead routes to admin)", es: "Formulario de eventos privados (lead llega al admin)" },
        { en: "Hours, location, Google Maps embed", es: "Horarios, ubicación, Google Maps embed" },
        { en: "Custom domain support on Pro", es: "Dominio personalizado con Pro" },
      ],
      execution: [
        {
          kicker: { en: "Step 1", es: "Paso 1" },
          title: { en: "Site is already live", es: "El sitio ya está en vivo" },
          body: {
            en: "useredy.com/venue/<your-slug> is published from day one. Share it as-is or customize.",
            es: "useredy.com/venue/<tu-slug> ya está publicado desde día uno. Compártelo así o personalízalo.",
          },
        },
        {
          kicker: { en: "Step 2", es: "Paso 2" },
          title: { en: "Drop the hero", es: "Subes el hero" },
          body: {
            en: "Upload a 10-second loop video (or a hero image). Add tagline copy. Boom — looks like you hired an agency.",
            es: "Subes un video en loop de 10 segundos (o imagen hero). Texto del tagline. Listo — se ve como agencia.",
          },
        },
        {
          kicker: { en: "Step 3", es: "Paso 3" },
          title: { en: "Publish an event", es: "Publicas evento" },
          body: {
            en: "When you go live on a new event, it appears in the 'Upcoming Events' feed automatically. Buy-ticket button included.",
            es: "Al publicar un evento, sale en el feed 'Próximos Eventos' automáticamente. Botón de comprar incluido.",
          },
        },
        {
          kicker: { en: "Step 4", es: "Paso 4" },
          title: { en: "Catch private bookings", es: "Capturas eventos privados" },
          body: {
            en: "The 'Private Events' form on the page lands as a lead in your admin. Reply from the same inbox. No more lost DMs.",
            es: "El formulario de 'Eventos Privados' cae como lead en tu admin. Respondes desde el mismo inbox. Sin DMs perdidos.",
          },
        },
      ],
      screenshots: [
        {
          src: "/marketing/features/venue-microsite/01-hero.png",
          alt: { en: "Venue microsite hero with looping video", es: "Hero del microsite con video en loop" },
          caption: { en: "Hero video, your branding.", es: "Video hero, tu marca." },
        },
        {
          src: "/marketing/features/venue-microsite/02-events.png",
          alt: { en: "Upcoming events feed on venue site", es: "Feed de eventos próximos en el sitio" },
          caption: { en: "Upcoming events — live sync.", es: "Eventos próximos — sync en vivo." },
        },
        {
          src: "/marketing/features/venue-microsite/03-inquiry.png",
          alt: { en: "Private event inquiry form", es: "Formulario de eventos privados" },
          caption: { en: "Private inquiries land in admin.", es: "Eventos privados caen en el admin." },
        },
      ],
    },
  },
];

export function getFeatureBySlug(slug: string): MarketingFeature | undefined {
  return features.find((f) => f.slug === slug);
}

export function getFeaturesByVertical(vertical: Vertical): MarketingFeature[] {
  return features.filter((f) => f.vertical === vertical);
}

export const featureSlugs = features.map((f) => f.slug);
